/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

/* global document */
/* eslint-disable no-empty */
/* eslint-disable no-bitwise */

// JSGPRO
import {
	CellRange, 
	// default as JSG,
	Selection,
} from '@cedalo/jsg-core';
import { createView } from '@cedalo/jsg-extensions/ui';
// JSGENDPRO

const opposedLine = (start, end) => {
	const lengthX = end.x - start.x;
	const lengthY = end.y - start.y;
	return {
		length: Math.sqrt(lengthX ** 2 + lengthY ** 2), angle: Math.atan2(lengthY, lengthX)
	};
};

const controlPoint = (current, previous, next, reverse) => {
	// When 'current' is the first or last point of the array
	// 'previous' or 'next' don't exist.
	// Replace with 'current'
	const p = previous || current;
	const n = next || current;
	// The smoothing ratio
	const smoothing = 0.2;
	// Properties of the opposed-line
	const o = opposedLine(p, n);
	// If is end-control-point, add PI to the angle to go backward
	const angle = o.angle + (reverse ? Math.PI : 0);
	const length = o.length * smoothing;
	// The control point position is relative to the current point
	const x = current.x + Math.cos(angle) * length;
	const y = current.y + Math.sin(angle) * length;
	return { x, y };
};

export default function SheetPlotViewFactory(JSG, ...args) {
	const {
		ChartRect,
		TextFormatAttributes,
		FormatAttributes,
		ChartFormat,
		MathUtils,
		Rectangle,
		Numbers
	} = JSG;

	class SheetPlotView extends JSG.NodeView {
		isNewChart() {
			return true;
		}

		onSelectionChange(selected) {
			if (!selected) {
				this.chartSelection = undefined;
				this.getGraphView().clearLayer('chartselection');
			}
		}

		drawBorder(graphics, format, rect) {
			super.drawBorder(graphics, format, rect);
		}

		setFormat(graphics, item, format, id) {
			const lineColor = format.lineColor ? format.lineColor : item.getTemplate()[id].format.lineColor;
			const fillColor = format.fillColor ? format.fillColor : item.getTemplate()[id].format.fillColor;
			const lineStyle = format.lineStyle === undefined ? item.getTemplate()[id].format.lineStyle :
				format.lineStyle;
			const lineWidth = format.lineWidth === undefined ? item.getTemplate()[id].format.lineWidth :
				format.lineWidth;
			const fillStyle = format.fillStyle === undefined ? item.getTemplate()[id].format.fillStyle :
				format.fillStyle;

			const line = this.setLineStyle(graphics, lineStyle);
			if (line) {
				graphics.setLineWidth(lineWidth);
				graphics.setLineColor(lineColor);
			}
			if (fillColor && fillStyle !== 0) {
				graphics.setFillColor(fillColor);
			}

			return {
				line, fill: fillStyle !== 0
			};
		}

		drawRect(graphics, rect, item, format, id) {
			const fi = this.setFormat(graphics, item, format, id);

			if (fi.fill) {
				if (format.transparency !== undefined) {
					graphics.setTransparency(format.transparency);
				}
				graphics.fillRectangle(rect.left, rect.top, rect.width, rect.height);
				if (format.transparency !== undefined) {
					graphics.setTransparency(100);
				}
			}
			if (fi.line) {
				graphics.drawRectangle(rect.left, rect.top, rect.width, rect.height);
			}
			return fi.line || fi.fill;
		}

		drawFill(graphics, format, rect) {
			super.drawFill(graphics, format, rect);

			const item = this.getItem();
			if (item._isFeedback) {
				return;
			}

			const { series } = item;
			const plotRect = item.plot.position;
			const chartValueRanges = item.getChartValueRanges();

			if (plotRect.height > 0 && plotRect.width > 0) {
				let drawAxes = false;
				const gauge = item.isGauge();

				series.forEach((serie) => {
					switch (serie.type) {
					case 'map':
					case 'pie':
					case 'doughnut':
						break;
					default:
						drawAxes = true;
						break;
					}
				});

				this.drawRect(graphics, plotRect, item, item.plot.format, 'plot');

				if (item.isGauge()) {
					const gaugeInfo = item.getGaugeInfo(plotRect);
					const first = item.getFirstVisibleSeries();
					const last = item.getLastVisibleSeries();
					const axes = item.getAxes(series[first]);
					const radii1 = item.getGaugeRadius(axes, gaugeInfo, item.series[first], first, axes.x.scale.min);
					const radii2 = item.getGaugeRadius(axes, gaugeInfo, item.series[last], last, axes.x.scale.max);

					graphics.beginPath();
					graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, radii2.xOuterRadius, radii2.yOuterRadius, 0,
						gaugeInfo.startAngle, gaugeInfo.startAngle + gaugeInfo.angle, false);
					graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, radii1.xInnerRadius, radii1.yInnerRadius, 0,
						gaugeInfo.startAngle + gaugeInfo.angle, gaugeInfo.startAngle, true);
					graphics.setFillColor(JSG.theme.tool);
					graphics.fill();
				}


				if (drawAxes) {
					this.drawValueRanges(graphics, plotRect, item, false);
					if (!gauge) {
						this.drawAxes(graphics, plotRect, item, true);
					}
					this.drawValueRanges(graphics, plotRect, item, true);
				}

				let lastPoints;

				series.forEach((serie, index) => {
					if (serie.visible) {
						switch (serie.type) {
						case 'pie':
						case 'doughnut':
							this.drawCircular(graphics, item, plotRect, serie, index);
							break;
							// JSGPRO
						case 'map':
							this.drawMap(graphics, item, plotRect, serie, index);
							break;
						case 'gauge':
							this.drawGauge(graphics, item, plotRect, serie, index, chartValueRanges);
							break;
						case 'boxplot':
							this.drawBoxPlot(graphics, item, plotRect, serie, index);
							break;
							// JSGENDPRO
						default:
							lastPoints =
								this.drawCartesian(graphics, item, plotRect, serie, index, lastPoints, chartValueRanges);
							drawAxes = true;
							break;
						}
					}
				});

				lastPoints = undefined;

				series.forEach((serie, index) => {
					if (serie.visible && item.hasSeriesDataLabel(serie)) {
						lastPoints = this.drawLabels(graphics, item, plotRect, serie, index, lastPoints, chartValueRanges);
					}
				});

				if (drawAxes) {
					this.drawAxes(graphics, plotRect, item, false);
					if (gauge) {
						this.drawAxes(graphics, plotRect, item, true);
					}
				}

				if (item.chart.hiLoLines.visible) {
					this.drawHiLoLines(graphics, item, plotRect);
				}
				if (item.chart.upBars.visible) {
					this.drawUpDownBars(graphics, item, plotRect);
				}
			}

			this.drawLegend(graphics, plotRect, item);
			this.drawTitle(graphics, item, item.title, 'title', 0);
			this.drawChartShapes(graphics, item, plotRect);

			item.actions.forEach((action) => {
				switch (action.title) {
				case 'mapzoomplus':
				case 'mapzoomminus':
					graphics.beginPath();
					graphics.setLineColor('#333333');
					graphics.setFillColor('#FFFFFF');
					graphics.rect(action.position.left, action.position.top, action.position.width,
						action.position.height);
					graphics.fill();
					graphics.moveTo(action.position.left + 100, action.position.top + action.position.height / 2);
					graphics.lineTo(action.position.right - 100, action.position.top + action.position.height / 2);
					if (action.title === 'mapzoomplus') {
						graphics.moveTo(action.position.left + action.position.width / 2, action.position.top + 100);
						graphics.lineTo(action.position.left + action.position.width / 2, action.position.bottom - 100);
					}
					graphics.stroke();
					break;
				case 'sysicon':
					if (item.chart.menuVisible) {
						graphics.beginPath();
						graphics.setFillColor('#999999');
						graphics.circle(action.position.left + action.position.width / 2, action.position.top + 225,
							50);
						graphics.circle(action.position.left + action.position.width / 2, action.position.top + 400,
							50);
						graphics.circle(action.position.left + action.position.width / 2, action.position.top + 575,
							50);
						graphics.fill();
					}
					break;
				default:
					graphics.setTextBaseline('middle');
					graphics.setFillColor('#444444');
					graphics.setTextAlignment(1);
					graphics.setFontName('Verdana');
					graphics.setFontSize('8');
					graphics.setFontStyle(0);
					graphics.setFont();
					graphics.fillText(action.title, action.position.left + action.position.width / 2,
						action.position.top + action.position.height / 2);
					break;
				}
			});
		}

		drawChartShapes(graphics, item, plotRect) {
			const range = item.getRangeInfo(item.chart.shapesJson);
			if (!range || item.series.length === 0) {
				return;
			}

			graphics.save();
			graphics.beginPath();
			graphics.rect(plotRect.left - 10, plotRect.top - 10, plotRect.width + 20, plotRect.height + 20);
			graphics.clip();

			this.drawShapesRange(graphics, range, plotRect);

			graphics.restore();
		}

		drawLegend(graphics, plotRect, item) {
			const margin = 200;
			const { legend } = item;
			const legendData = item.getLegend();
			const cs = graphics.getCoordinateSystem();

			if (!legend.visible) {
				return;
			}

			this.drawRect(graphics, legend.position, item, legend.format, 'legend');

			const template = item.getTemplate();
			item.setFont(graphics, legend.format, 'legend', 'top', TextFormatAttributes.TextAlignment.LEFT);
			const textSize = item.measureText(graphics, graphics.getCoordinateSystem(), legend.format, 'legend', 'X');
			let x = legend.position.left + margin;
			let y = legend.position.top + margin;
			const title = String(legend.formulaTitle.getValue());
			if (title.length) {
				item.setFont(graphics, legend.format, 'legend', 'top', TextFormatAttributes.TextAlignment.CENTER);
				const fontColor = legend.format.fontColor || template.legend.format.fontColor || template.font.color;
				graphics.setFillColor(fontColor);
				graphics.setTextBaseline('top');
				graphics.fillText(title, legend.position.centerX, y);
				y += textSize.height + margin;
				item.setFont(graphics, legend.format, 'legend', 'top', TextFormatAttributes.TextAlignment.LEFT);
			}

			let textPos = margin * 4;
			let maxHeight = 0;
			let type = 'bar';
			let fill = true;
			let line = true;
			let column = 0;
			const horzChart = item.isHorizontalChart();

			if (item.isMap()) {
				// JSGPRO
				const fontColor = legend.format.fontColor || template.legend.format.fontColor || template.font.color;

				if (legend.align === 'right' || legend.align === 'middleright' || legend.align === 'left' || legend.align === 'middleleft') {
					graphics.beginPath();
					const gradient = graphics.createLinearGradient(x, legend.position.bottom - margin, x, y);
					if (gradient) {
						gradient.addColorStop(0, legendData[0].color);
						gradient.addColorStop(1, legendData[2].color);
						graphics._context2D.fillStyle = gradient;
						graphics.rect(x, y, 300, legend.position.height - margin * 2);
						graphics.fill();
					}
					graphics.setFillColor(fontColor);
					graphics.setTextBaseline('top');
					graphics.fillText(legendData[2].name, x + 200 + margin, y);
					graphics.setTextBaseline('middle');
					graphics.fillText(legendData[1].name, x + 200 + margin,
						legend.position.top + legend.position.height / 2);
					graphics.setTextBaseline('bottom');
					graphics.fillText(legendData[0].name, x + 200 + margin, legend.position.bottom - margin);
				} else {
					graphics.beginPath();
					const gradient = graphics.createLinearGradient(x + margin, y, legend.position.right - margin, y);
					gradient.addColorStop(0, legendData[0].color);
					gradient.addColorStop(1, legendData[2].color);
					graphics._context2D.fillStyle = gradient;
					graphics.rect(x, y, legend.position.width - margin * 2, 300);
					graphics.fill();
					graphics.setFillColor(fontColor);
					graphics.setTextBaseline('top');
					graphics.setTextAlignment(0);
					graphics.fillText(legendData[0].name, x, y + 200 + margin);
					graphics.setTextAlignment(1);
					graphics.fillText(legendData[1].name, legend.position.left + legend.position.width / 2,
						y + 200 + margin);
					graphics.setTextAlignment(2);
					graphics.fillText(legendData[2].name, legend.position.right - margin, y + 200 + margin);
				}
				// JSGENDPRO
			} else {
				legendData.forEach((entry, index) => {
					graphics.beginPath();
					if (entry.series) {
						let lineWidth;
						let lineColor;
						let fillColor;
						let lineStyle;
						let fillStyle;
						if (item.chart.varyByCategories && entry.series.points[index] && entry.series.points[index].format) {
							const pointFormat = entry.series.points[index].format;
							const serieFormat = entry.series.format;
							lineColor =
								pointFormat.lineColor || serieFormat.lineColor || template.series.getLineForIndex(
									index);
							lineWidth = pointFormat.lineWidth || serieFormat.lineWidth || template.series.linewidth;
							lineStyle = pointFormat.lineStyle || serieFormat.lineStyle;
							fillColor =
								pointFormat.fillColor || serieFormat.fillColor || template.series.getFillForIndex(
									index);
							fillStyle = pointFormat.fillStyle || serieFormat.fillStyle;
							serieFormat.transparency = pointFormat.transparency || entry.series.format.transparency;
						} else {
							const seriesIndex = item.chart.varyByCategories ? index : item.series.indexOf(entry.series);
							lineWidth = entry.series.format.lineWidth || template.series.linewidth;
							lineColor = entry.series.format.lineColor || template.series.getLineForIndex(seriesIndex);
							fillColor = entry.series.format.fillColor || template.series.getFillForIndex(seriesIndex);
							lineStyle = entry.series.format.lineStyle === undefined ? template.series.linestyle :
								entry.series.format.lineStyle;
							fillStyle = entry.series.format.fillStyle === undefined ? template.series.fillstyle :
								entry.series.format.fillStyle;
						}

						graphics.setLineWidth(lineWidth);
						line = this.setLineStyle(graphics, lineStyle);
						fill = fillStyle > 0;
						graphics.setFillStyle(fillStyle);
						graphics.setLineColor(lineColor);
						graphics.setFillColor(fillColor);
						type = entry.series.type;
					} else {
						graphics.setLineWidth(
							entry.lineWidth !== undefined ? entry.lineWidth : template.series.linewidth);
						line = this.setLineStyle(graphics, template.series.linestyle);
						fill = template.series.fillstyle > 0;
						if (entry.lineColor) {
							graphics.setLineColor(entry.lineColor);
						} else {
							graphics.setLineColor(entry.color);
						}
						graphics.setFillColor(entry.color);
					}

					switch (type) {
					case 'line':
					case 'profile':
					case 'scatter':
						graphics.moveTo(x, y + textSize.height / 2);
						graphics.lineTo(x + margin * 3, y + textSize.height / 2);
						if (fill) {
							this.fill(graphics, entry.series.format.transparency);
						}
						if (line) {
							graphics.stroke();
						}
						if (entry.series.marker.style !== undefined) {
							const mRect = new ChartRect(x, y + textSize.height / 10, x + margin * 3,
								y + (textSize.height * 9) / 10);
							graphics.clearLineDash();
							graphics.setLineWidth(-1);
							const seriesIndex = item.chart.varyByCategories ? index : item.series.indexOf(entry.series);
							const lineColor = entry.series.marker.lineColor || item.getTemplate().series
								.getLineForIndex(seriesIndex);
							const fillColor = entry.series.marker.fillColor || item.getTemplate().series
								.getFillForIndex(seriesIndex);
							graphics.setLineColor(lineColor);
							graphics.setFillColor(fillColor);
							graphics.beginPath();
							this.drawMarker(graphics, item, mRect, entry.series, index, undefined, {
								x: x + margin * 1.5, y: y + textSize.height / 2
							}, 3, horzChart, lineColor, fillColor);
							graphics.fill();
							graphics.stroke();
						}
						break;
					case 'area':
					case 'column':
						// JSGPRO
						// eslint-disable-next-line no-fallthrough
					case 'gauge':
					case 'state':
					case 'boxplot':
					case 'waterfall':
					case 'funnelbar':
					case 'funnelcolumn':
						// JSGENDPRO
						// eslint-disable-next-line no-fallthrough
					case 'bar':
					case 'pie':
					case 'doughnut':
						if (item.isCircular()) {
							if (entry.series.format.lineColor === undefined) {
								graphics.setLineColor('#FFFFFF');
							} else {
								graphics.setLineColor(entry.series.format.lineColor);
							}
						}

						graphics.rect(x, y + textSize.height / 10, margin * 3, (textSize.height * 9) / 10);
						if (fill) {
							if (entry.series) {
								this.fill(graphics, entry.series.format.transparency);
							} else {
								graphics.fill();
							}
						}
						if (line) {
							graphics.stroke();
						}
						break;
					case 'bubble':
						textPos = margin * 2;
						graphics.circle(x + margin / 2, y + textSize.height / 2, (textSize.height * 2) / 5);
						if (fill) {
							this.fill(graphics, entry.series.format.transparency);
						}
						if (line) {
							graphics.stroke();
						}
						break;
					default:
						break;
					}

					const fontColor = legend.format.fontColor || template.legend.format.fontColor || template.font.color;
					const size = item.measureText(graphics, cs, legend.format, 'legend', String(entry.name));

					graphics.setFillColor(fontColor);
					graphics.fillMultiLineText(entry.name, x + textPos, y + (size.height * 1.1) / 2, textSize.height);

					maxHeight = Math.max(maxHeight, size.height);
					if (legend.align === 'right' || legend.align === 'middleright' || legend.align === 'left' || legend.align === 'middleleft') {
						y += size.height + textSize.height * 0.5;
					} else if (item.legend.columns === undefined) {
						x += size.width + margin * 2 + textPos;
					} else if (column === item.legend.columns - 1) {
						x = legend.position.left + margin;
						y += maxHeight + textSize.height * 0.5;
						column = -1;
						maxHeight = 0;
					} else {
						x += item.legend.maxTextWidth;
					}
					column += 1;
				});
			}

			graphics.setLineWidth(-1);
		}

		drawAxes(graphics, plotRect, item, grid) {
			item.xAxes.forEach((axis) => {
				if (axis.visible) {
					this.drawTitle(graphics, item, axis.title, 'axisTitle', axis.isVertical() ? Math.PI_2 : 0);
					this.drawAxis(graphics, plotRect, item, axis, grid);
				}
			});

			item.yAxes.forEach((axis) => {
				if (axis.visible) {
					this.drawTitle(graphics, item, axis.title, 'axisTitle', axis.isVertical() ? Math.PI_2 : 0);
					this.drawAxis(graphics, plotRect, item, axis, grid);
				}
			});
		}

		drawAxis(graphics, plotRect, item, axis, grid) {
			if (!axis.position || !axis.scale || (grid && !axis.gridVisible)) {
				return;
			}

			let fi;
			const labelAngle = axis.format.fontRotation === undefined ? 0 :
				JSG.MathUtils.toRadians(-axis.format.fontRotation);
			// JSGPRO
			const gaugeInfo = axis.align === 'radialoutside' || axis.align === 'radialinside' ?
				item.getGaugeInfo(plotRect) : null;
			// JSGENDPRO
			const cs = graphics.getCoordinateSystem();

			graphics.beginPath();
			if (grid) {
				fi = this.setFormat(graphics, item, axis.formatGrid, 'axisgrid');
			} else {
				// draw axis line
				fi = this.setFormat(graphics, item, axis.format, 'axis');
				item.setFont(graphics, axis.format, 'axis', 'top', JSG.TextFormatAttributes.TextAlignment.CENTER);
				switch (axis.align) {
				case 'left':
					graphics.moveTo(axis.position.right, axis.position.top);
					graphics.lineTo(axis.position.right, axis.position.bottom);
					break;
				case 'right':
					graphics.moveTo(axis.position.left, axis.position.top);
					graphics.lineTo(axis.position.left, axis.position.bottom);
					break;
				case 'top':
					graphics.moveTo(axis.position.left, axis.position.bottom);
					graphics.lineTo(axis.position.right, axis.position.bottom);
					break;
				case 'bottom':
					graphics.moveTo(axis.position.left, axis.position.top);
					graphics.lineTo(axis.position.right, axis.position.top);
					break;
				default:
					break;
				}
				if (fi.line) {
					graphics.stroke();
				}
			}

			const refLabel = item.getDataSourceInfoAxis(axis);
			let current = item.getAxisStart(refLabel, axis);
			const final = item.getAxisEnd(axis);
			let last;
			// JSGPRO
			let first = true;
			// JSGENDPRO
			let width = 0;
			let pos;
			let plot;
			let text;
			let size;
			const space = axis.size ? axis.size.space : undefined;
			const lineSize = item.measureText(graphics, cs, axis.format, 'axis', 'X', true);

			while (current.value <= final) {
				if (axis.type === 'category' && (grid || !axis.betweenTicks ? current.value > axis.scale.max :
					current.value >= axis.scale.max)) {
					break;
				}

				pos = item.scaleToAxis(axis, current.value, undefined, grid);

				if (!grid) {
					if (axis.type === 'category' && refLabel) {
						const index = Math.floor(current.value);
						if (axis.uniqueLabels && axis.uniqueLabels.length > index) {
							text = axis.uniqueLabels[index];
						} else {
							text = item.getLabel(refLabel, axis, index);
						}
					} else if (axis.format && axis.format.numberFormat && !axis.format.linkNumberFormat) {
						text = item.formatNumber(current.value, axis.format);
					} else {
						text = item.formatNumber(current.value,
							axis.scale.format && !axis.format.linkNumberFormat ? axis.scale.format : {
								numberFormat: axis.format.linkedNumberFormat,
								localCulture: axis.format.linkedLocalCulture
							});
					}
					if (space) {
						text = item.wrapText(graphics, cs, axis.format, 'axis', text, space);
					}
					size = item.measureText(graphics, cs, axis.format, 'axis', text, false, space);
					width = 0;
					switch (axis.align) {
					case 'left':
					case 'right':
						if (Math.abs(Math.abs(labelAngle) - Math.PI_2) < Math.PI / 20) {
							width = size.width * Math.sin(labelAngle);
						}
						break;
					case 'top':
					case 'bottom':
						if (Math.abs(labelAngle) < Math.PI / 20) {
							width = size.width * Math.cos(labelAngle);
						}
						break;
					default:
						break;
					}
				}

				switch (axis.align) {
					// JSGPRO
				case 'radialoutside':
					if (gaugeInfo.angle % (Math.PI * 2) > 0.00001 || first === false) {
						plot = gaugeInfo.startAngle + pos * gaugeInfo.angle;
						let x = gaugeInfo.xc + Math.cos(plot) * gaugeInfo.xRadius;
						let y = gaugeInfo.yc + Math.sin(plot) * gaugeInfo.xRadius;
						if (grid) {
							const radius = gaugeInfo.xRadius * item.chart.hole;
							graphics.moveTo(x, y);
							x = gaugeInfo.xc + Math.cos(plot) * radius;
							y = gaugeInfo.yc + Math.sin(plot) * radius;
							graphics.lineTo(x, y);
						} else {
							if (axis.valueRanges.length && axis.valueRangesVisible && item.chart.gaugePointer === false) {
								x += Math.cos(plot) * (size.width / 2 + 600);
								y += Math.sin(plot) * (size.height / 2 + 600);
							} else {
								x += Math.cos(plot) * (size.width / 2 + axis.labelDistance);
								y += Math.sin(plot) * (size.height / 2 + axis.labelDistance);
							}

							this.drawRotatedText(graphics, `${text}`, x, y, labelAngle, lineSize.height);
						}
					}
					first = false;
					break;
				case 'radialinside':
					if (gaugeInfo.angle % (Math.PI * 2) > 0.00001 || first === false) {
						const radius = gaugeInfo.xRadius * item.chart.hole;
						plot = gaugeInfo.startAngle + pos * gaugeInfo.angle;
						let x = gaugeInfo.xc + Math.cos(plot) * radius;
						let y = gaugeInfo.yc + Math.sin(plot) * radius;
						if (grid) {
							graphics.moveTo(x, y);
							x = gaugeInfo.xc + Math.cos(plot) * gaugeInfo.xRadius;
							y = gaugeInfo.yc + Math.sin(plot) * gaugeInfo.xRadius;
							graphics.lineTo(x, y);
						} else {
							if (axis.valueRanges.length && axis.valueRangesVisible && item.chart.gaugePointer === false) {
								x -= Math.cos(plot) * (size.width / 2 + 600);
								y -= Math.sin(plot) * (size.height / 2 + 600);
							} else {
								x -= Math.cos(plot) * (size.width / 2 + axis.labelDistance);
								y -= Math.sin(plot) * (size.height / 2 + axis.labelDistance);
							}
							this.drawRotatedText(graphics, `${text}`, x, y, labelAngle, lineSize.height);
						}
					}
					first = false;
					break;
					// JSGENDPRO
				case 'left':
				case 'right':
					plot = plotRect.bottom - pos * plotRect.height;
					if (grid) {
						graphics.moveTo(plotRect.left, plot);
						graphics.lineTo(plotRect.right, plot);
					} else {
						const check = axis.invert ? last === undefined || plot - width / 2 + 100 > last :
							last === undefined || plot + width / 2 + 100 < last;
						if (check) {
							const x = axis.align === 'left' ?
								axis.position.right - axis.labelDistance - size.width / 2 :
								axis.position.left + axis.labelDistance + size.width / 2;
							this.drawRotatedText(graphics, `${text}`, x, plot, labelAngle, lineSize.height);
							last = axis.invert ? plot + width / 2 : plot - width / 2;
						}
					}
					break;
				case 'top':
				case 'bottom':
					plot = plotRect.left + pos * plotRect.width;
					if (grid) {
						graphics.moveTo(plot, plotRect.top);
						graphics.lineTo(plot, plotRect.bottom);
					} else {
						const check = axis.invert ? last === undefined || plot + width / 2 + 100 < last :
							last === undefined || plot - width / 2 - 100 > last;
						if (check) {
							const y = axis.align === 'bottom' ?
								axis.position.top + axis.labelDistance + size.height / 2 :
								axis.position.bottom - axis.labelDistance - size.height / 2;
							this.drawRotatedText(graphics, `${text}`, plot, y, labelAngle, lineSize.height);
							last = axis.invert ? plot - width / 2 : plot + width / 2;
						}
					}
					break;
				default:
					break;
				}

				current = item.incrementScale(refLabel, axis, current);
			}
			if (grid && fi.line) {
				graphics.stroke();
			}
		}

		drawValueRanges(graphics, plotRect, item, lines) {
			graphics.save();
			if (!item.isGauge()) {
				graphics.beginPath();
				graphics.rect(plotRect.left - 10, plotRect.top - 10, plotRect.width + 20, plotRect.height + 20);
				graphics.clip();
			}

			item.xAxes.forEach((axis) => {
				if (axis.visible) {
					this.drawValueRange(graphics, plotRect, item, axis, lines);
				}
			});

			item.yAxes.forEach((axis) => {
				if (axis.visible) {
					this.drawValueRange(graphics, plotRect, item, axis, lines);
				}
			});

			graphics.restore();
		}

		drawValueRange(graphics, plotRect, item, axis, lines) {
			if (!axis.valueRanges.length || !axis.valueRangesVisible) {
				return;
			}

			item.setFont(graphics, axis.format, 'axis', 'bottom', TextFormatAttributes.TextAlignment.LEFT);

			const gaugeInfo = item.getGaugeInfo(plotRect);

			axis.valueRanges.forEach((range) => {
				const term = range.formula.getTerm();
				const label = item.getParamValue(term, 0);
				const from = item.getParamValue(term, 1);
				const to = item.getParamValue(term, 2);
				const width = item.getParamValue(term, 3);

				let startBegin = item.scaleToAxis(axis, from, undefined, true);
				let startEnd = item.scaleToAxis(axis, from + width, undefined, true);
				let endBegin = item.scaleToAxis(axis, to, undefined, true);
				let endEnd = item.scaleToAxis(axis, to + width, undefined, true);
				switch (axis.align) {
				case 'radialinside':
				case 'radialoutside': {
					const first = item.getFirstVisibleSeries();
					const last = item.getLastVisibleSeries();
					const axes = item.getAxes(item.series[first]);
					const radii1 = item.getGaugeRadius(axes, gaugeInfo, item.series[first], first, axes.x.scale.min);
					const radii2 = item.getGaugeRadius(axes, gaugeInfo, item.series[last], last, axes.x.scale.max);

					const size = item.scaleSizeToAxis(axes.y, to - from);
					let angle = Math.abs(size) * gaugeInfo.angle;
					let currentAngle;

					if (axes.y.invert) {
						angle = -angle;
					}
					let pos = (to - axis.scale.min) / (axis.scale.max - axis.scale.min);
					if (axes.y.invert) {
						pos = 1 - pos;
					}
					if (size < 0) {
						currentAngle = gaugeInfo.startAngle + pos * gaugeInfo.angle;
					} else {
						currentAngle = axes.y.invert ? gaugeInfo.startAngle + (pos + size) * gaugeInfo.angle :
							gaugeInfo.startAngle + (pos - size) * gaugeInfo.angle;
					}

					graphics.beginPath();

					let innerRadius;
					let outerRadius;
					if (item.chart.gaugePointer) {
						innerRadius = radii1.xInnerRadius;
						outerRadius = radii2.xOuterRadius;
					} else if (axes.y.align === 'radialoutside') {
						innerRadius = radii2.xOuterRadius + 100;
						outerRadius = radii2.xOuterRadius + 400;
					} else {
						innerRadius = radii1.xInnerRadius - 400;
						outerRadius = radii1.xInnerRadius - 100;
					}

					graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, outerRadius, outerRadius, 0,
						Math.max(gaugeInfo.startAngle,
							Math.min(gaugeInfo.endAngle, currentAngle + (axes.y.invert ? angle : 0))),
						Math.max(gaugeInfo.startAngle,
							Math.min(gaugeInfo.endAngle, currentAngle + (axes.y.invert ? 0 : angle))),
						false);
					graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, innerRadius, innerRadius, 0,
						Math.max(gaugeInfo.startAngle,
							Math.min(gaugeInfo.endAngle, currentAngle + (axes.y.invert ? 0 : angle))),
						Math.max(gaugeInfo.startAngle,
							Math.min(gaugeInfo.endAngle, currentAngle + (axes.y.invert ? angle : 0))),
						true);

					graphics.setFillColor(range.format.fillColor);
					this.fill(graphics);
					break;
				}
				case 'left':
				case 'right':
					startBegin = plotRect.bottom - startBegin * plotRect.height;
					startEnd = plotRect.bottom - startEnd * plotRect.height;
					endBegin = plotRect.bottom - endBegin * plotRect.height;
					endEnd = plotRect.bottom - endEnd * plotRect.height;
					if (label && lines) {
						graphics.setFillColor('#AAAAAA');
						graphics.fillText(label, plotRect.left + 100, startBegin - 100);
					}
					graphics.beginPath();
					if (width === 0 && lines) {
						graphics.setLineColor(range.format.fillColor);
						graphics.moveTo(plotRect.left, startBegin);
						graphics.lineTo(plotRect.right, endBegin);
						graphics.stroke();
					} else if (width > 0 && !lines) {
						graphics.setFillColor(range.format.fillColor);
						graphics.setTransparency(range.format.transparency);
						graphics.moveTo(plotRect.left, startBegin);
						graphics.lineTo(plotRect.right, endBegin);
						graphics.lineTo(plotRect.right, endEnd);
						graphics.lineTo(plotRect.left, startEnd);
						graphics.fill();
						graphics.setTransparency(100);
					}
					break;
				case 'top':
				case 'bottom':
					startBegin = plotRect.left + startBegin * plotRect.width;
					startEnd = plotRect.left + startEnd * plotRect.width;
					endBegin = plotRect.left + endBegin * plotRect.width;
					endEnd = plotRect.left + endEnd * plotRect.width;
					graphics.beginPath();
					if (width === 0 && lines) {
						graphics.setLineColor(range.format.fillColor);
						graphics.moveTo(startBegin, plotRect.bottom);
						graphics.lineTo(endBegin, plotRect.top);
						graphics.stroke();
					} else if (width > 0 && !lines) {
						graphics.setFillColor(range.format.fillColor);
						graphics.setTransparency(range.format.transparency);
						graphics.moveTo(startBegin, plotRect.bottom);
						graphics.lineTo(endBegin, plotRect.top);
						graphics.lineTo(endEnd, plotRect.top);
						graphics.lineTo(startEnd, plotRect.bottom);
						graphics.fill();
						graphics.setTransparency(100);
					}
					break;
				default:
					break;
				}
			});
		}

		drawRotatedText(graphics, text, x, y, angle, lineHeight) {
			if (angle) {
				graphics.save();
				graphics.startGroup();
				graphics.translate(x, y);
				graphics.rotate(-angle);
				graphics.fillMultiLineText(text, 0, 0, lineHeight);
				graphics.endGroup();
				graphics.restore();
			} else if (lineHeight) {
				graphics.fillMultiLineText(text, x, y, lineHeight);
			} else {
				graphics.fillText(text, x, y);
			}
		}

		setLineStyle(graphics, lineStyle) {
			if (lineStyle === undefined) {
				lineStyle = 1;
			}

			if (lineStyle === 'none') {
				lineStyle = 0;
			}

			if (lineStyle === graphics.getLineStyle()) {
				return lineStyle > 0;
			}

			graphics.setLineStyle(lineStyle);
			if (lineStyle > 1) {
				graphics.applyLineDash();
			} else {
				graphics.clearLineDash();
			}

			return lineStyle > 0;
		}

		fill(graphics, transparency) {
			if (transparency !== undefined) {
				graphics.setTransparency(transparency);
			}
			graphics.fill();
			if (transparency !== undefined) {
				graphics.setTransparency(100);
			}
		}

		initDataFormat(item, serie) {
			return {
				def: false,
				tmpl: item.getTemplate().series,
				lineColor: serie.format.lineColor,
				lineWidth: serie.format.lineWidth,
				lineStyle: serie.format.lineStyle,
				fillColor: serie.format.fillColor,
				fillStyle: serie.format.fillStyle,
				transparency: serie.format.transparency
			};
		}

		setDataPointFormat(graphics, item, serie, serieFormat, seriesIndex, pointIndex, varyByCategories, circular) {
			let pointFormat;
			let lineColor;
			let lineStyle;
			let lineWidth;
			let fillColor;
			let fillStyle;

			if (pointIndex !== undefined && serie.points[pointIndex] && serie.points[pointIndex].format) {
				pointFormat = serie.points[pointIndex].format;
				lineColor = pointFormat.lineColor || serieFormat.lineColor;
				lineWidth = pointFormat.lineWidth || serieFormat.lineWidth;
				lineStyle = pointFormat.lineStyle || serieFormat.lineStyle;
				fillColor = pointFormat.fillColor || serieFormat.fillColor;
				fillStyle = pointFormat.fillStyle || serieFormat.fillStyle;
				serieFormat.transparency = pointFormat.transparency || serie.format.transparency;
				serieFormat.def = false;
			} else {
				if (serieFormat.def === true && !varyByCategories) {
					return;
				}
				lineColor = serieFormat.lineColor;
				lineWidth = serieFormat.lineWidth;
				lineStyle = serieFormat.lineStyle;
				fillColor = serieFormat.fillColor;
				fillStyle = serieFormat.fillStyle;
				serieFormat.transparency = serie.format.transparency;
				serieFormat.def = true;
			}

			if (circular) {
				graphics.setLineColor(lineColor || '#FFFFFF');
			} else {
				graphics.setLineColor(
					lineColor || serieFormat.tmpl.getLineForIndex(varyByCategories ? pointIndex : seriesIndex));
			}
			graphics.setLineWidth(lineWidth || serieFormat.tmpl.linewidth);
			serieFormat.line = this.setLineStyle(graphics, lineStyle);
			graphics.setFillColor(
				fillColor || serieFormat.tmpl.getFillForIndex(varyByCategories ? pointIndex : seriesIndex));
			serieFormat.fill = (fillStyle === undefined ? serieFormat.tmpl.fillstyle : fillStyle) > 0;
		}

		drawCircular(graphics, item, plotRect, serie, seriesIndex) {
			const ref = item.getDataSourceInfo(serie);
			const value = {};
			const pieInfo = item.getPieInfo(ref, serie, plotRect, seriesIndex);
			const fillRect = new Rectangle();

			graphics.setLineJoin('round');

			if (serie.type === 'pie' && item.series.length > 1 && ref.yName) {
				item.setFont(graphics, serie.format, 'series', 'bottom',
					TextFormatAttributes.TextAlignment.CENTER);
				const offset = (pieInfo.rect.height - pieInfo.yRadius * 2) / 3;
				graphics.fillText(ref.yName, pieInfo.rect.left + pieInfo.rect.width / 2, pieInfo.rect.top - 500 + offset);
			}

			let index = 0;
			const useFormat = this.initDataFormat(item, serie);

			// 3d sides
			if (serie.type === 'pie' && item.chart.rotation < Math.PI / 2) {
				this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, 0, item.chart.varyByCategories,
					true);

				graphics.beginPath();
				graphics.moveTo(pieInfo.xc, pieInfo.yc);
				graphics.lineTo(pieInfo.xc, pieInfo.yc + pieInfo.height);
				graphics.lineTo(pieInfo.xc + pieInfo.xRadius * Math.cos(pieInfo.startAngle),
					pieInfo.yc + pieInfo.height + pieInfo.yRadius * Math.sin(pieInfo.startAngle));
				graphics.lineTo(pieInfo.xc + pieInfo.xRadius * Math.cos(pieInfo.startAngle),
					pieInfo.yc + pieInfo.yRadius * Math.sin(pieInfo.startAngle));

				if (useFormat.fill) {
					this.fill(graphics, useFormat.transparency);
				}
				if (useFormat.line) {
					graphics.stroke();
				}

				while (item.getValue(ref, index, value)) {
					index += 1;
				}
				this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, index - 1,
					item.chart.varyByCategories, true);

				graphics.beginPath();
				graphics.moveTo(pieInfo.xc, pieInfo.yc);
				graphics.lineTo(pieInfo.xc, pieInfo.yc + pieInfo.height);
				graphics.lineTo(pieInfo.xc + pieInfo.xRadius * Math.cos(pieInfo.endAngle),
					pieInfo.yc + pieInfo.height + pieInfo.yRadius * Math.sin(pieInfo.endAngle));
				graphics.lineTo(pieInfo.xc + pieInfo.xRadius * Math.cos(pieInfo.endAngle),
					pieInfo.yc + pieInfo.yRadius * Math.sin(pieInfo.endAngle));
				if (useFormat.fill) {
					this.fill(graphics, useFormat.transparency);
				}
				if (useFormat.line) {
					graphics.stroke();
				}
			}

			for (let i = 0; i < 2; i += 1) {
				let currentAngle = pieInfo.startAngle;
				index = 0;
				while (item.getValue(ref, index, value)) {
					if (value.x !== undefined && value.y !== undefined) {
						this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, index,
							item.chart.varyByCategories, true);

						const angle = (Math.abs(value.y) / pieInfo.sum) * (pieInfo.endAngle - pieInfo.startAngle);
						switch (serie.type) {
						case 'doughnut': {
							graphics.beginPath();
							graphics.ellipse(pieInfo.xc, pieInfo.yc, pieInfo.xOuterRadius, pieInfo.yOuterRadius, 0,
								currentAngle, currentAngle + angle, false);
							graphics.ellipse(pieInfo.xc, pieInfo.yc, pieInfo.xInnerRadius, pieInfo.yInnerRadius, 0,
								currentAngle + angle, currentAngle, true);
							if (i) {
								if (useFormat.line) {
									graphics.stroke();
								}
							} else if (useFormat.fill) {
								this.fill(graphics, useFormat.transparency);
							}
							break;
						}
						case 'pie':
							graphics.beginPath();
							graphics.ellipse(pieInfo.xc, pieInfo.yc, pieInfo.xRadius, pieInfo.yRadius, 0, currentAngle,
								currentAngle + angle, false);
							graphics.lineTo(pieInfo.xc, pieInfo.yc);
							graphics.closePath();

							if (i) {
								if (useFormat.line) {
									graphics.stroke();
								}
							} else if (useFormat.fill) {
								this.fill(graphics, useFormat.transparency);
							}

							// 3d front
							if (item.chart.rotation < Math.PI / 2) {
								if ((currentAngle >= 0 && currentAngle <= Math.PI) || (currentAngle + angle >= 0 && currentAngle + angle <= Math.PI) || angle >= Math.PI) {
									graphics.beginPath();
									graphics.ellipse(pieInfo.xc, pieInfo.yc, pieInfo.xRadius, pieInfo.yRadius, 0,
										Math.max(0, currentAngle), Math.min(Math.PI, currentAngle + angle), false);
									const x1 = pieInfo.xc + pieInfo.xRadius * Math.cos(
										Math.min(Math.PI, currentAngle + angle));
									let y = pieInfo.yc + pieInfo.height + pieInfo.yRadius * Math.sin(
										Math.min(Math.PI, currentAngle + angle));
									graphics.lineTo(x1, y);

									graphics.ellipse(pieInfo.xc, pieInfo.yc + pieInfo.height, pieInfo.xRadius,
										pieInfo.yRadius, 0, Math.min(Math.PI, currentAngle + angle),
										Math.max(0, currentAngle), true);
									const x2 = pieInfo.xc + pieInfo.xRadius * Math.cos(Math.max(0, currentAngle));
									y = pieInfo.yc + pieInfo.yRadius * Math.sin(Math.max(0, currentAngle));
									graphics.lineTo(x2, y);

									if (i) {
										if (useFormat.line) {
											graphics.stroke();
										}
									} else {
										fillRect.set(pieInfo.rect.left, y, pieInfo.rect.width, y);
										let fillColor;
										if (serie.points[index] && serie.points[index].format) {
											const pointFormat = serie.points[index].format;
											fillColor = pointFormat.fillColor || serie.format.fillColor;
										} else {
											fillColor = serie.format.fillColor || useFormat.tmpl.getFillForIndex(
												item.chart.varyByCategories ? index : seriesIndex);
										}
										graphics.setGradientLinear(fillRect,
											fillColor || item.getTemplate().series.getFillForIndex(index), '#333333', 0,
											0);
										if (useFormat.fill) {
											this.fill(graphics, useFormat.transparency);
										}
										graphics.setFillColor(fillColor);
									}
								}
							}
							break;
						default:
							break;
						}
						currentAngle += angle;
					}
					index += 1;
				}
			}

			graphics.setLineJoin('miter');
		}

		// JSGPRO

		drawMapChart(graphics, view, item, serie, feature, sheet, selection, info, ref, mapIndex, mapInfo, tooltip) {
			const pt = item.getFeatureCenter(feature, mapInfo, serie, ref, mapIndex);

			if (pt.radius !== undefined) {
				selection
					.getAt(0)
					.set(info._x1, info._y1 + mapIndex, info._x2, info._y1 + mapIndex);

				mapInfo.chartNode.setChartType(serie.map.chartType);
				mapInfo.chartNode.series = [];
				mapInfo.chartNode.updateSeriesFromRange(undefined, sheet, selection, serie.map.chartType, true, false,
					undefined, undefined, undefined, true);
				mapInfo.chartNode.xAxes[0].gridVisible = false;
				const serieMap = mapInfo.chartNode.series[0];
				serieMap.seriesMapsMin = serie.mapsMin;
				serieMap.seriesMapsMax = serie.mapsMax;
				// mapInfo.chartNode.yAxes[0].
				let rect;
				if (tooltip) {
					mapInfo.chartNode.setSize(8000, 6000);
					mapInfo.chartNode.layout();
					pt.x -= 4000;
					pt.y -= 3000;
					graphics.translate(pt.x, pt.y);
					rect = new Rectangle(pt.x, pt.y, 8000, 6000);
					view.drawFill(graphics, item.getFormat(), rect);
					graphics.translate(-pt.x, -pt.y);
				} else {
					mapInfo.chartNode.setSize(pt.radius, pt.radius);
					mapInfo.chartNode.layout();
					rect = new ChartRect(pt.x - pt.radius, pt.y - pt.radius, pt.x + pt.radius, pt.y + pt.radius);
					switch (serieMap.type) {
					case 'pie':
					case 'doughnut':
						view.drawCircular(graphics, mapInfo.chartNode, rect, serieMap, 0);
						break;
					default:
						graphics.beginPath();
						view.drawCartesian(graphics, mapInfo.chartNode, rect, serieMap, 0);
						graphics.setLineWidth(-1);
						graphics.setLineColor('#000000');
						graphics.moveTo(rect.left, rect.top);
						graphics.lineTo(rect.left, rect.bottom);
						graphics.lineTo(rect.right, rect.bottom);
						graphics.stroke();
						break;
					}
				}
			}
		}

		getMap(serie, item, ref) {
			if (!serie.map) {
				return false;
			}
			if (serie.map.name === 'sheet') {
				serie.map.mapData = item.getSheetMap(ref);
				serie.map.bounds = item.getMapBox(serie.map.mapData.features);
			} else if (!serie.map.mapData) {
				if (serie.map.requesting === undefined) {
					serie.map.requesting = true;
					const xhr = JSG._createRequest(`maps/${serie.map.name}`, {
						onload(response) {
							try {
								serie.map.mapData = JSON.parse(response);
								serie.map.bounds = item.getMapBox(serie.map.mapData.features);
								JSG.NotificationCenter.getInstance()
									.send(new JSG.Notification(JSG.GRAPH_INVALID_NOTIFICATION, this));
							} catch (e) {

							}
						}, onerror() {
							JSG.debug.logError(`Failed to load geojson`);
						}
					});
					xhr.send();
				}
				return false;
			}

			return true;
		}

		drawMap(graphics, item, plotRect, serie, seriesIndex) {
			const ref = item.getDataSourceInfo(serie);

			if (!this.getMap(serie, item, ref)) {
				graphics.setTextBaseline('bottom');
				graphics.setFillColor('#CCCCCC');
				graphics.setTextAlignment(0);
				graphics.fillText('Loading Maps...', plotRect.left, plotRect.bottom - 200);
				return;
			}

			let pt = {};
			const features = serie.map.mapData.features;
			const mapInfo = item.getMapInfo(plotRect, serie, ref);
			const axes = item.getAxes(serie);

			if (!mapInfo) {
				return;
			}

			graphics.save();
			graphics.beginPath();
			graphics.rect(plotRect.left - 10, plotRect.top - 10, plotRect.width + 20, plotRect.height + 20);
			graphics.clip();

			const color = serie.format.fillColor || item.getTemplate().series.getFillForIndex(seriesIndex);
			const lineColor = serie.format.lineColor || item.getTemplate().series.getLineForIndex(seriesIndex);
			const lineWidth = serie.format.lineWidth === undefined ? item.getTemplate().linewidth :
				serie.format.lineWidth;

			graphics.setLineColor(lineColor);
			graphics.setLineWidth(lineWidth);
			graphics.setFillColor(color);

			const drawFeature = (feature, index) => {
				graphics.setLineWidth(lineWidth);
				graphics.setLineColor(lineColor);
				if (mapInfo.dispColor) {
					graphics.setFillColor('#FFFFFF');
				} else {
					graphics.setFillColor(color);
				}
				if (feature.properties) {
					const value = {};
					if (index !== -1 && item.getValue(ref, index, value)) {
						if (mapInfo.dispLine) {
							graphics.setLineWidth(Math.max(-1, value.y));
						}
						switch (feature.geometry.type) {
						case 'Point':
						case 'Polygon':
						case 'MultiPolygon':
							if (mapInfo.dispColor || mapInfo.dispColorValue) {
								if (mapInfo.dispColor) {
									if (item.chart.dataMode === 'datainterrupt' && value.y === undefined) {
										graphics.setFillColor('#CCCCCC');
									} else {
										const fact = item.scaleToAxis(axes.y, value.y, undefined, false);
										const col = item.colorShade(fact, '#FFFFFF', color, true);
										graphics.setFillColor(col);
									}
								} else {
									graphics.setFillColor(value.pureY);
								}
							}
							break;
						default:
							break;
						}
					} else if (item.chart.dataMode === 'datainterrupt' && value.y === undefined) {
						graphics.setFillColor('#CCCCCC');
					}
					if (item.chart.dataMode !== 'dataignore' || (index !== -1 && value.y !== undefined)) {
						if (serie.format.fillStyle !== 0 && feature.geometry.type !== 'LineString' && feature.geometry.type !== 'MultiLineString') {
							graphics.fill();
						}
						graphics.stroke();
					}
				}
			};

			const value = {};
			item.getValue(ref, 0, value);
			const info = value === '#er' ? undefined : value.yInfo;
			const sheet = info ? info.sheet : undefined;
			let maxSum = 0;
			let view;
			const selection = info ? new Selection(sheet) : undefined;

			if (selection) {
				selection.setAt(0, new CellRange(sheet, 0, 0));
			}

			// get maximum sum of chart values
			if (mapInfo.dispChart) {
				view = createView(mapInfo.chartNode);
				serie.mapsMin = Number.MAX_VALUE;
				serie.mapsMax = -Number.MAX_VALUE;
				serie.maxSum = 0;
				features.forEach((feature) => {
					const index = item.findMapIndex(feature.properties, serie, mapInfo.labels);
					if (index !== -1) {
						let index2 = 0;
						const value2 = {};
						while (item.getValue(ref, index2, value2)) {
							let sum = 0;
							if (value2.yExtra) {
								// eslint-disable-next-line no-loop-func
								value2.yExtra.forEach(val => {
									if (Numbers.isNumber(val)) {
										serie.mapsMin = Math.min(val, serie.mapsMin);
										serie.mapsMax = Math.max(val, serie.mapsMax);
										sum += Math.abs(val);
									}
								});
								maxSum = Math.max(maxSum, sum);
							}
							index2 += 1;
						}
					}
				});
				serie.maxSum = maxSum;
			} else {
				serie.mapsMin = undefined;
				serie.mapsMax = undefined;
				serie.maxSum = 0;
			}

			// Loop through each “feature”
			features.forEach((feature) => {
				const { geometry } = feature;
				const index = item.findMapIndex(feature.properties, serie, mapInfo.labels);
				if (mapInfo.dispChart) {
					if (index !== -1 && selection) {
						this.drawMapChart(graphics, view, item, serie, feature, sheet, selection, info, ref, index,
							mapInfo, false);
					}
				} else {
					graphics.beginPath();
					item.enumerateMapCoordinates(geometry, (coordinate, idx, final) => {
						if (geometry.type === 'Point' || mapInfo.dispRadius) {
							pt = item.getFeatureCenter(feature, mapInfo, serie, ref, index);
							this.drawMarkerPath(graphics, plotRect, pt, serie.marker.style, pt.radius * 2, false);
						} else {
							pt.x = mapInfo.xOff + (coordinate[0] - mapInfo.bounds.xMin) * mapInfo.scale;
							pt.y = mapInfo.yOff + (mapInfo.bounds.yMax - coordinate[1]) * mapInfo.scale;
							if (idx) {
								graphics.lineTo(pt.x, pt.y);
							} else {
								graphics.moveTo(pt.x, pt.y);
							}
						}
						if (final && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
							graphics.closePath();
						}
					});
					drawFeature(feature, index);
				}
			});

			graphics.restore();
		}

		drawGauge(graphics, item, plotRect, serie, seriesIndex, chartValueRanges) {
			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);

			if (!ref || !axes) {
				return;
			}

			const value = {};
			const gaugeInfo = item.getGaugeInfo(plotRect);
			const info = {
				serie, seriesIndex, index: 0, categories: axes.y.categories
			};

			graphics.setLineJoin('round');
			let index = 0;
			let radii = item.getGaugeRadius(axes, gaugeInfo, serie, seriesIndex, 0);
			const varyByTreshold = item.chart.varyByThreshold !== 'none' && chartValueRanges && chartValueRanges.length;

			const useFormat = this.initDataFormat(item, serie);

			while (item.getValue(ref, index, value)) {
				info.index = index;
				if (value.x !== undefined && value.y !== undefined) {
					radii = item.getGaugeRadius(axes, gaugeInfo, serie, seriesIndex, index);
					this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, index,
						item.chart.varyByCategories, true);
					if (item.series.length === 1 && axes.x.scale.max === 0) {
						useFormat.line = false;
					}

					const section = item.getGaugeSection(axes, gaugeInfo, value, info);

					graphics.beginPath();

					if (item.chart.gaugePointer) {
						graphics.setLineColor(JSG.theme.border);
						graphics.setLineWidth(50);
						graphics.setLineStyle(0);
						graphics.setFillColor(JSG.theme.border);

						const radius = radii.pointerRadius;
						let pos = (value.y - axes.y.scale.min) / (axes.y.scale.max - axes.y.scale.min);
						if (axes.y.invert) {
							pos = 1 - pos;
						}
						const currentAngle = gaugeInfo.startAngle + pos * gaugeInfo.angle;
						const x = radius * Math.cos(currentAngle);
						const y = radius * Math.sin(currentAngle);

						graphics.circle(gaugeInfo.xc, gaugeInfo.yc, 200);
						graphics.fill();
						graphics.beginPath();

						const width = serie.pointerType === 'arrow' ? 250 : 200;
						const length = 200;
						const alphaCosW = (Math.cos(currentAngle) * width) / 2;
						const alphaCosL = Math.cos(currentAngle) * length;
						const alphaSinW = (Math.sin(currentAngle) * width) / 2;
						const alphaSinL = Math.sin(currentAngle) * length;

						switch (serie.pointerType) {
						case 'line':
							graphics.moveTo(gaugeInfo.xc, gaugeInfo.yc);
							graphics.lineTo(gaugeInfo.xc + x, gaugeInfo.yc + y);
							graphics.stroke();
							break;
						case 'narrowingline':
							graphics.moveTo(gaugeInfo.xc + x, gaugeInfo.yc + y);
							graphics.lineTo(gaugeInfo.xc + alphaSinW, gaugeInfo.yc - alphaCosW);
							graphics.lineTo(gaugeInfo.xc - alphaSinW, gaugeInfo.yc + alphaCosW);
							graphics.fill();
							break;
						case 'arrow':
							graphics.moveTo(gaugeInfo.xc, gaugeInfo.yc);
							graphics.lineTo(gaugeInfo.xc + x, gaugeInfo.yc + y);
							graphics.stroke();
							graphics.beginPath();
							graphics.moveTo(gaugeInfo.xc + x - alphaCosL + alphaSinW,
								gaugeInfo.yc + y - alphaSinL - alphaCosW);
							graphics.lineTo(gaugeInfo.xc + x, gaugeInfo.yc + y);
							graphics.lineTo(gaugeInfo.xc + x - alphaCosL - alphaSinW,
								gaugeInfo.yc + y - alphaSinL + alphaCosW);
							graphics.closePath();

							graphics.fill();
							break;
						default:
							break;
						}
					} else {
						if (varyByTreshold && item.chart.varyByThreshold === 'colorchange') {
							let fillColor;
							useFormat.line = false;
							chartValueRanges.some((range) => {
								if (Numbers.isNumber(value.y) && value.y >= range.from && value.y < range.to) {
									fillColor = range.color;
									return true;
								}
								return false;
							});
							if (fillColor === undefined) {
								graphics.setFillColor(
									serie.format.fillColor || item.getTemplate().series.getFillForIndex(seriesIndex));
								graphics.setLineColor(
									serie.format.lineColor || item.getTemplate().series.getLineForIndex(seriesIndex));
							} else {
								graphics.setFillColor(fillColor);
								graphics.setLineColor(fillColor);
							}
						}
						graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, radii.xOuterRadius, radii.yOuterRadius, 0,
							section.currentAngle + (axes.y.invert ? section.angle : 0),
							section.currentAngle + (axes.y.invert ? 0 : section.angle), false);
						graphics.ellipse(gaugeInfo.xc, gaugeInfo.yc, radii.xInnerRadius - 20, radii.yInnerRadius - 20,
							0, section.currentAngle + (axes.y.invert ? 0 : section.angle),
							section.currentAngle + (axes.y.invert ? section.angle : 0), true);
						if (useFormat.line) {
							// graphics.stroke();
						}
						if (useFormat.fill) {
							this.fill(graphics, useFormat.transparency);
						}
					}
				}
				index += 1;
			}

			graphics.setLineJoin('miter');
		}

		drawBoxPlot(graphics, item, plotRect, serie, seriesIndex) {
			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);

			if (!ref || !axes) {
				return;
			}

			const barWidth = item.getBarWidth(axes, serie, plotRect);
			let barInfo;
			const info = {
				serie, seriesIndex, index: 0, categories: axes.y.categories
			};
			const pt = { x: 0, y: 0 };
			const pt2 = { x: 0, y: 0 };

			graphics.save();

			graphics.beginPath();
			graphics.rect(plotRect.left - 10, plotRect.top - 10, plotRect.width + 20, plotRect.height + 20);
			graphics.clip();

			// collect values for each category
			const values = item.getBoxPlotValues(ref, axes);

			const darkLine = serie.format.lineColor || this.lightenDarkenColor(
				item.getTemplate().series.getLineForIndex(seriesIndex), -30);
			const useFormat = this.initDataFormat(item, serie);

			Object.entries(values).forEach(([, valueSet]) => {
				this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, 0, false, false);

				graphics.setLineColor(darkLine);
				graphics.setLineWidth(-1);

				graphics.beginPath();

				const { median, q1, q3, minIndex, maxIndex, average } = item.getBoxPlotFigures(valueSet);

				barInfo = item.getBarInfo(axes, serie, seriesIndex, valueSet[0].x, q3 - q1, barWidth);

				pt.x = item.scaleToAxis(axes.x, valueSet[0].x, undefined, false);
				const x = pt.x;
				pt.y = item.scaleToAxis(axes.y, q3, info, false);
				item.toPlot(serie, plotRect, pt);
				if (barInfo.height) {
					graphics.rect(pt.x + barInfo.offset, pt.y, barWidth - barInfo.margin,
						-barInfo.height * plotRect.height);
					if (useFormat.fill) {
						graphics.fill();
					}
					if (useFormat.line) {
						graphics.stroke();
					}
					graphics.beginPath();
				}

				if (valueSet.length > 3) {
					// draw whiskers
					pt.x = x;
					pt.y = item.scaleToAxis(axes.y, valueSet[maxIndex].y, info, false);
					item.toPlot(serie, plotRect, pt);
					if (plotRect.containsPoint(pt)) {
						graphics.moveTo(pt.x + barInfo.offset + (barWidth - barInfo.margin) / 3, pt.y);
						graphics.lineTo(pt.x + barInfo.offset + ((barWidth - barInfo.margin) * 2) / 3, pt.y);
						pt2.x = x;
						pt2.y = item.scaleToAxis(axes.y, q3, info, false);
						item.toPlot(serie, plotRect, pt2);
						graphics.moveTo(pt.x + barInfo.offset + (barWidth - barInfo.margin) / 2, pt.y);
						graphics.lineTo(pt.x + barInfo.offset + (barWidth - barInfo.margin) / 2, pt2.y);
					}

					pt.x = x;
					pt.y = item.scaleToAxis(axes.y, valueSet[minIndex].y, info, false);
					item.toPlot(serie, plotRect, pt);
					if (plotRect.containsPoint(pt)) {
						graphics.moveTo(pt.x + barInfo.offset + barWidth / 3, pt.y);
						graphics.lineTo(pt.x + barInfo.offset + (barWidth * 2) / 3 - barInfo.margin, pt.y);
						pt2.x = x;
						pt2.y = item.scaleToAxis(axes.y, q1, info, false);
						item.toPlot(serie, plotRect, pt2);
						graphics.moveTo(pt.x + barInfo.offset + (barWidth - barInfo.margin) / 2, pt.y);
						graphics.lineTo(pt.x + barInfo.offset + (barWidth - barInfo.margin) / 2, pt2.y);
					}
				}

				// draw median
				pt.x = x;
				pt.y = item.scaleToAxis(axes.y, median, info, false);
				item.toPlot(serie, plotRect, pt);
				if (plotRect.containsPoint(pt)) {
					graphics.moveTo(pt.x + barInfo.offset, pt.y);
					graphics.lineTo(pt.x + barInfo.offset + barWidth - barInfo.margin, pt.y);
				}

				graphics.setFillColor(darkLine);

				if (valueSet.length > 3) {
					valueSet.forEach((val, valIndex) => {
						if (valIndex !== maxIndex && valIndex !== minIndex) {
							pt.x = x;
							pt.y = item.scaleToAxis(axes.y, val.y, info, false);
							item.toPlot(serie, plotRect, pt);
							pt.x += barInfo.offset + (barWidth - barInfo.margin) / 2;
							if (plotRect.containsPoint(pt)) {
								if ((valIndex > minIndex && valIndex < maxIndex && serie.innerPoints) || valIndex < minIndex || (valIndex > maxIndex && serie.outerPoints)) {
									graphics.moveTo(pt.x, pt.y);
									graphics.circle(pt.x, pt.y, 50);
								}
							}
						}
					});
				}

				// draw average
				if (serie.average) {
					pt.x = x;
					pt.y = item.scaleToAxis(axes.y, average, info, false);
					item.toPlot(serie, plotRect, pt);
					pt.x += barInfo.offset + (barWidth - barInfo.margin) / 2;
					if (plotRect.containsPoint(pt)) {
						graphics.moveTo(pt.x - 100, pt.y - 100);
						graphics.lineTo(pt.x + 100, pt.y + 100);
						graphics.moveTo(pt.x + 100, pt.y - 100);
						graphics.lineTo(pt.x - 100, pt.y + 100);
					}
				}

				if (useFormat.fill) {
					graphics.fill();
				}
				if (useFormat.line) {
					graphics.stroke();
				}
				graphics.beginPath();
			});

			graphics.restore();
		}

		// JSGENDPRO

		drawCartesian(graphics, item, plotRect, serie, seriesIndex, lastPoints, chartValueRanges) {
			let index = 0;
			let barInfo;
			const value = {};
			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);

			if (!ref || !axes) {
				return undefined;
			}

			if (!item.isZoomed(serie)) {
				graphics.setTextBaseline('top');
				graphics.setFillColor('#CCCCCC');
				graphics.setTextAlignment(0);
				graphics.setFontName('Verdana');
				graphics.setFontSize('8');
				graphics.setFontStyle(0);
				graphics.setFont();
				graphics.fillText('Retrieving Data...', 100, 100);
			}

			const isLineType = item.isLineType(serie);
			let lineBorder = 10;
			if (isLineType) {
				const lw = serie.format.lineWidth === undefined ? item.getTemplate().series.linewidth :
					serie.format.lineWidth;
				lineBorder = Math.max(10, (lw + 10) / 2);
			}

			graphics.save();
			graphics.beginPath();
			graphics.rect(plotRect.left - lineBorder, plotRect.top - lineBorder, plotRect.width + lineBorder * 2, plotRect.height + lineBorder * 2);
			graphics.clip();

			graphics.beginPath();

			const barWidth = item.getBarWidth(axes, serie, plotRect);
			const points = [];
			let newLine = true;
			let xFirst;
			let xLast;
			// eslint-disable-next-line
			let yLast = 0;
			// eslint-disable-next-line
			// JSGPRO
			let valueSum = 0;
			// JSGENDPRO
			let noLast = false;
			const pt = { x: 0, y: 0 };
			const ptPrev = { x: 0, y: 0 };
			const ptNext = { x: 0, y: 0 };
			const ptLast = { x: 0, y: 0 };
			const info = {
				serie, seriesIndex, index: 0, categories: axes.y.categories
			};
			const varyByCategories = item.chart.varyByCategories && !isLineType;
			const varyByTreshold = item.allowThreshold(serie) && item.chart.varyByThreshold !== 'none' && chartValueRanges && chartValueRanges.length;
			const heatInfo = serie.type === 'heatmap' ? this.prepareHeatmap(graphics, chartValueRanges, plotRect) : undefined;
			let gradient;
			const horzChart = item.isHorizontalChart();

			if (varyByTreshold && item.chart.varyByThreshold === 'gradient') {
				const {min, max} = axes.y.scale;
				pt.y = item.scaleToAxis(axes.y, min, info, false);
				item.toPlot(serie, plotRect, pt);
				ptPrev.y = item.scaleToAxis(axes.y, max, info, false);
				item.toPlot(serie, plotRect, ptPrev);
				if (horzChart) {
					gradient = graphics.createLinearGradient(pt.x, 0, ptPrev.x, 0);
				} else {
					gradient = graphics.createLinearGradient(0, pt.y, 0, ptPrev.y);
				}
				const defColor = serie.format.fillColor || item.getTemplate().series.getFillForIndex(seriesIndex);
				chartValueRanges.forEach((range, inx) => {
					if (String(range.color).length) {
						const valFrom = Math.max(0,
							Math.min(1, (range.from - min) / (max - min)));
						const valTo = Math.max(0,
							Math.min(1, (range.to - min) / (max - min)));
						try {
							if (inx === 0) {
								if (valFrom > 0) {
									gradient.addColorStop(0, defColor);
									gradient.addColorStop(valFrom, defColor);
								}
								gradient.addColorStop(valFrom, String(range.color));
								gradient.addColorStop(valTo, String(range.color));
							} else if (inx === chartValueRanges.length - 1) {
								gradient.addColorStop(valFrom, String(range.color));
								gradient.addColorStop(valTo, String(range.color));
								if (valTo < 1) {
									gradient.addColorStop(valTo, defColor);
									gradient.addColorStop(1, defColor);
								}

							} else {
								gradient.addColorStop(valTo, String(range.color));
								gradient.addColorStop(valFrom, String(range.color));
							}
						} catch (e) {
							gradient.addColorStop(Math.min(1, valFrom), '#CCCCCC');
						}
					}
				});
			}

			const useFormat = this.initDataFormat(item, serie);

			while (item.getValue(ref, index, value)) {
				info.index = index;
				this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, index, varyByCategories, false);
				if (item.chart.dataMode === 'datainterrupt' || (value.x !== undefined && value.y !== undefined)) {
					const threshVal = serie.type === 'bubble' ? value.c : value.y
					if (varyByTreshold && threshVal !== undefined) {
						let fillColor;
						useFormat.line = false;
						switch (item.chart.varyByThreshold) {
						case 'colorchange':
							chartValueRanges.some((range) => {
								if (Numbers.isNumber(threshVal) && threshVal >= range.from && threshVal < range.to) {
									fillColor = range.color;
									return true;
								}
								return false;
							});
							if (fillColor === undefined) {
								graphics.setFillColor(
									serie.format.fillColor || item.getTemplate().series.getFillForIndex(seriesIndex));
								graphics.setLineColor(
									serie.format.lineColor || item.getTemplate().series.getLineForIndex(seriesIndex));
							} else {
								graphics.setFillColor(fillColor);
								graphics.setLineColor(fillColor);
							}
							break;
						case 'gradient':
							if (gradient) {
								graphics._context2D.fillStyle = gradient;
							}
							break;
						default:
							break;
						}
					}

					if (serie.type === 'column' || serie.type === 'bar') {
						value.y = Math.max(axes.y.scale.min, value.y);
					}
					pt.x = item.scaleToAxis(axes.x, value.x, undefined, false);
					pt.y = item.scaleToAxis(axes.y, value.y, info, false);
					item.toPlot(serie, plotRect, pt);
					switch (serie.type) {
					case 'profile':
						if (item.chart.dataMode === 'datainterrupt' && (value.x === undefined || value.y === undefined)) {
							newLine = true;
						} else if (newLine) {
							graphics.moveTo(pt.x, pt.y);
							newLine = false;
							xFirst = pt.x;
							xLast = pt.x;
						} else {
							if (serie.smooth) {
								item.getPlotPoint(axes, ref, info, value, index, -1, ptLast);
								item.toPlot(serie, plotRect, ptLast);

								const midX = (ptLast.x + pt.x) / 2;
								const midY = (ptLast.y + pt.y) / 2;
								const cpY1 = (midY + ptLast.y) / 2;
								const cpY2 = (midY + pt.y) / 2;
								graphics.quadraticCurveTo(ptLast.x, cpY1, midX, midY);
								graphics.quadraticCurveTo(pt.x, cpY2, pt.x, pt.y);
							} else {
								graphics.lineTo(pt.x, pt.y);
							}
							xLast = pt.x;
						}
						break;
					case 'heatmap':
						if (value.x !== undefined && value.y !== undefined && value.c !== undefined) {
							heatInfo.context.globalAlpha = Math.min(Math.max(value.c / axes.y.cMaxData, 0.05), 1);
							heatInfo.context.drawImage(heatInfo.circle,
								heatInfo.cs.logToDeviceX(pt.x - plotRect.left) - heatInfo.r,
								heatInfo.cs.logToDeviceX(pt.y - plotRect.top) - heatInfo.r);
						}
						break;
					case 'bubble':
						if (value.x !== undefined && value.y !== undefined && value.c !== undefined) {
							const radius = item.scaleBubble(axes.y, plotRect, serie, value.c);
							graphics.moveTo(pt.x + radius, pt.y);
							graphics.circle(pt.x, pt.y, radius);
						}
						break;
					case 'scatter':
						if (item.chart.dataMode === 'datainterrupt' && (value.x === undefined || value.y === undefined)) {
							newLine = true;
						} else if (newLine) {
							graphics.moveTo(pt.x, pt.y);
							newLine = false;
							xFirst = pt.x;
							xLast = pt.x;
						} else {
							if (serie.smooth) {
								item.getPlotPoint(axes, ref, info, value, index, 1, ptNext);
								item.getPlotPoint(axes, ref, info, value, index, -1, ptLast);
								item.getPlotPoint(axes, ref, info, value, index, -2, ptPrev);
								item.toPlot(serie, plotRect, ptPrev);
								item.toPlot(serie, plotRect, ptLast);
								item.toPlot(serie, plotRect, ptNext);

								const cp1 = controlPoint(ptLast, ptPrev, pt);
								const cp2 = controlPoint(pt, ptLast, ptNext, true);

								graphics.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, pt.x, pt.y);
							} else {
								graphics.lineTo(pt.x, pt.y);
							}

							xLast = pt.x;
						}
						break;
					case 'area':
					case 'line':
						if (item.chart.dataMode === 'datainterrupt' && (value.x === undefined || value.y === undefined)) {
							newLine = true;
						} else if (newLine) {
							graphics.moveTo(pt.x, pt.y);
							newLine = false;
							noLast = true;
							xFirst = pt.x;
							xLast = pt.x;
						} else {
							if (serie.smooth && !item.chart.step) {
								item.getPlotPoint(axes, ref, info, value, index, -1, ptLast);
								item.toPlot(serie, plotRect, ptLast);

								const midX = (ptLast.x + pt.x) / 2;
								const midY = (ptLast.y + pt.y) / 2;
								const cpX1 = (midX + ptLast.x) / 2;
								const cpX2 = (midX + pt.x) / 2;
								graphics.quadraticCurveTo(cpX1, ptLast.y, midX, midY);
								graphics.quadraticCurveTo(cpX2, pt.y, pt.x, pt.y);
							} else if (item.chart.step) {
								item.getPlotPoint(axes, ref, info, value, index, -1, ptLast);
								item.toPlot(serie, plotRect, ptLast);
								graphics.lineTo(pt.x, ptLast.y);
								graphics.lineTo(pt.x, pt.y);
							} else {
								graphics.lineTo(pt.x, pt.y);
							}

							xLast = pt.x;
						}
						if (serie.type === 'area') {
							if (newLine) {
								pt.y = item.scaleToAxis(axes.y, 0, undefined, false);
								pt.y = plotRect.bottom - pt.y * plotRect.height;
								graphics.lineTo(xLast, pt.y);
								graphics.lineTo(xFirst, pt.y);
								graphics.closePath();
								if (useFormat.fill) {
									this.fill(graphics, useFormat.transparency);
								}
								if (useFormat.line) {
									graphics.stroke();
								}
								graphics.beginPath();
							}
							if (item.chart.stacked) {
								if (item.chart.step && !noLast) {
									points.push({
										x: pt.x, y: ptLast.y
									});
								}
								noLast = false;
								points.push({
									x: pt.x, y: pt.y
								});
							}
						}
						break;
					case 'column':
						if (value.x !== undefined && value.y !== undefined) {
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								if (item.chart.segments) {
									graphics.verticalSegmentRect(pt.x + barInfo.offset, pt.y, Math.max(25, barWidth - barInfo.margin),
										-barInfo.height * plotRect.height, plotRect.height, useFormat.line);
								} else {
									graphics.rect(pt.x + barInfo.offset, pt.y, Math.max(25, barWidth - barInfo.margin),
										-barInfo.height * plotRect.height);
								}
							}
						}
						break;
						// JSGPRO
					case 'waterfall':
						if (value.x !== undefined && value.y !== undefined) {
							if (serie.autoSum && index) {
								const lastVal = { x: 0, y: 0 };
								item.getValue(ref, index - 1, lastVal);
								value.y -= lastVal.y === undefined ? 0 : lastVal.y;
							}
							if (serie.points[index] && serie.points[index].pointSum) {
								valueSum = value.y;
							} else {
								valueSum += value.y;
							}
							pt.y = item.scaleToAxis(axes.y, valueSum, info, false);
							pt.y = plotRect.bottom - pt.y * plotRect.height;
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								if (index) {
									graphics.setLineColor('#AAAAAA');
									graphics.moveTo(xLast, yLast);
									if (serie.points[index] && serie.points[index].pointSum) {
										graphics.lineTo(pt.x + barInfo.offset, pt.y);
									} else {
										graphics.lineTo(pt.x + barInfo.offset, yLast);
									}
									graphics.stroke();
									graphics.beginPath();
								}
								let lineColor = serie.format.lineColor;
								let fillColor = serie.format.fillColor;
								if (serie.points[index] && serie.points[index].format) {
									const pointFormat = serie.points[index].format;
									lineColor = pointFormat.lineColor || lineColor;
									fillColor = pointFormat.fillColor || fillColor;
								}
								if (lineColor === undefined) {
									if (serie.points[index] && serie.points[index].pointSum) {
										if (chartValueRanges && chartValueRanges.length > 2) {
											graphics.setLineColor(chartValueRanges[2].color);
										} else {
											graphics.setLineColor('#CCCCCC');
										}
									} else if (value.y >= 0) {
										if (chartValueRanges && chartValueRanges.length) {
											graphics.setLineColor(chartValueRanges[0].color);
										} else {
											graphics.setLineColor('#2e7031');
										}
									} else if (chartValueRanges && chartValueRanges.length > 1) {
										graphics.setLineColor(chartValueRanges[1].color);
									} else {
										graphics.setLineColor('#bc1818');
									}
								} else {
									graphics.setLineColor(lineColor);
								}
								if (fillColor === undefined) {
									if (serie.points[index] && serie.points[index].pointSum) {
										if (chartValueRanges && chartValueRanges.length > 2) {
											graphics.setFillColor(chartValueRanges[2].color);
										} else {
											graphics.setFillColor('#CCCCCC');
										}
									} else if (value.y >= 0) {
										if (chartValueRanges && chartValueRanges.length) {
											graphics.setFillColor(chartValueRanges[0].color);
										} else {
											graphics.setFillColor('#2e7031');
										}
									} else if (chartValueRanges && chartValueRanges.length > 1) {
										graphics.setFillColor(chartValueRanges[1].color);
									} else {
										graphics.setFillColor('#bc1818');
									}
								}
								graphics.rect(pt.x + barInfo.offset, pt.y, barWidth - barInfo.margin,
									-barInfo.height * plotRect.height);
								xLast = pt.x + barInfo.offset + barWidth - barInfo.margin;
								yLast = pt.y;
							}
						}
						break;
					case 'funnelcolumn':
						if (value.x !== undefined && value.y !== undefined) {
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								const height = Math.abs(barInfo.height * plotRect.height);
								if (item.chart.seriesLines.visible) {
									let barInfoNext = barInfo;
									if (item.getValue(ref, index + 1, value)) {
										barInfoNext =
											item.getBarInfo(axes, serie, seriesIndex, index + 1, value.y, barWidth);
									}
									const height2 = Math.abs(barInfoNext.height * plotRect.height);
									graphics.moveTo(pt.x + barInfo.offset,
										plotRect.top + plotRect.height / 2 - height / 2);
									graphics.lineTo(pt.x + barInfo.offset,
										plotRect.top + plotRect.height / 2 + height / 2);
									graphics.lineTo(pt.x + barInfo.offset + barWidth,
										plotRect.top + plotRect.height / 2 + height2 / 2);
									graphics.lineTo(pt.x + barInfo.offset + barWidth,
										plotRect.top + plotRect.height / 2 - height2 / 2);
									graphics.lineTo(pt.x + barInfo.offset,
										plotRect.top + plotRect.height / 2 - height / 2);
								} else {
									graphics.rect(pt.x + barInfo.offset,
										plotRect.top + plotRect.height / 2 - height / 2, barWidth - 25, height);
								}
							}
						}
						break;
					case 'state':
						if (value.x !== undefined && value.y !== undefined) {
							graphics.beginPath();
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								useFormat.def = false;
								this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, index, varyByCategories, false);
								let fillState;
								let lineState;
								if (!chartValueRanges.some((range) => {
									if (Numbers.isNumber(value.c) && value.c >= range.from && value.c < range.to) {
										fillState = range.color;
										lineState = range.color;
										return true;
									}
									return false;
								})) {
									const states = String(value.c).split(';');
									fillState = states.length ? states[0] : undefined;
									lineState = states.length > 1 ? states[1] : undefined;
								}

								if (fillState && fillState.length) {
									graphics.setFillColor(fillState);
								}
								if (lineState && lineState.length) {
									graphics.setLineColor(lineState);
								} else if (fillState && fillState.length) {
									graphics.setLineColor(fillState);
								}
								if (item.chart.period) {
									item.getPlotPoint(axes, ref, info, value, index, 1, ptNext);
									item.toPlot(serie, plotRect, ptNext);
									graphics.rect(pt.x /* + barInfo.offset*/, pt.y, Math.abs(ptNext.x - pt.x) + 20,
										-barInfo.height * plotRect.height + 20);
								} else {
									graphics.rect(pt.x + barInfo.offset, pt.y, barWidth + 10,
										-barInfo.height * plotRect.height);
								}
								graphics.fill();
								graphics.stroke();
							}
						}
						break;
					case 'funnelbar':
						if (value.x !== undefined && value.y !== undefined) {
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								const width = Math.abs(barInfo.height * plotRect.width);
								if (item.chart.seriesLines.visible) {
									let barInfoNext = barInfo;
									if (item.getValue(ref, index + 1, value)) {
										barInfoNext =
											item.getBarInfo(axes, serie, seriesIndex, index + 1, value.y, barWidth);
									}
									const width2 = Math.abs(barInfoNext.height * plotRect.width);
									graphics.moveTo(plotRect.left + plotRect.width / 2 - width / 2,
										pt.y + barInfo.offset);
									graphics.lineTo(plotRect.left + plotRect.width / 2 + width / 2,
										pt.y + barInfo.offset);
									graphics.lineTo(plotRect.left + plotRect.width / 2 + width2 / 2,
										pt.y + barInfo.offset + barWidth);
									graphics.lineTo(plotRect.left + plotRect.width / 2 - width2 / 2,
										pt.y + barInfo.offset + barWidth);
									graphics.lineTo(plotRect.left + plotRect.width / 2 - width / 2,
										pt.y + barInfo.offset);
								} else {
									graphics.rect(plotRect.left + plotRect.width / 2 - width / 2, pt.y + barInfo.offset,
										width, barWidth - 50);
								}
							}
						}
						break;
						// JSGENDPRO
					case 'bar':
						if (value.x !== undefined && value.y !== undefined) {
							barInfo = item.getBarInfo(axes, serie, seriesIndex, index, value.y, barWidth);
							if (barInfo.height) {
								if (item.chart.segments) {
									graphics.horizontalSegmentRect(pt.x, pt.y + barInfo.offset, barInfo.height * plotRect.width,
										barWidth - barInfo.margin, plotRect.width, useFormat.line);
								} else {
									graphics.rect(pt.x, pt.y + barInfo.offset, barInfo.height * plotRect.width,
										barWidth - barInfo.margin);
								}
							}
						}
						break;
					default:
						graphics.setTextBaseline('center');
						graphics.setFillColor('#CCCCCC');
						graphics.setTextAlignment(1);
						graphics.setFontName('Verdana');
						graphics.setFontSize('8');
						graphics.setFontStyle(0);
						graphics.setFont();
						graphics.fillText('Chart Type not available', plotRect.center.x, plotRect.center.y);
						break;
					}
				}
				index += 1;
				if (serie.type === 'waterfall' || (!isLineType && serie.type !== 'area' && serie.type !== 'state')) {
					if (useFormat.fill) {
						this.fill(graphics, useFormat.transparency);
					}
					if (useFormat.line) {
						graphics.stroke();
					}
					graphics.beginPath();
				}
			}

			this.setDataPointFormat(graphics, item, serie, useFormat, seriesIndex, undefined, false, false);

			if (serie.type === 'area') {
				if (useFormat.line) {
					graphics.stroke();
				}
				if (seriesIndex && item.chart.stacked && lastPoints) {
					let point;
					for (let i = lastPoints.length - 1; i >= 0; i -= 1) {
						point = lastPoints[i];
						if (serie.smooth && i < lastPoints.length - 1) {
							const pLast = lastPoints[i + 1];
							const midX = (pLast.x + point.x) / 2;
							const midY = (pLast.y + point.y) / 2;
							const cpX1 = (midX + pLast.x) / 2;
							const cpX2 = (midX + point.x) / 2;
							graphics.quadraticCurveTo(cpX1, pLast.y, midX, midY);
							graphics.quadraticCurveTo(cpX2, point.y, point.x, point.y);
						} else {
							graphics.lineTo(point.x, point.y);
						}
					}
				} else {
					pt.y = item.scaleToAxis(axes.y, 0, undefined, false);
					graphics.lineTo(xLast, plotRect.bottom - pt.y * plotRect.height);
					graphics.lineTo(xFirst, plotRect.bottom - pt.y * plotRect.height);
				}
				graphics.closePath();
			}


			if (useFormat.fill && serie.type === 'area') {
				if (varyByTreshold) {
					useFormat.line = false;
					switch (item.chart.varyByThreshold) {
					case 'colorchange':
						break;
					case 'gradient':
						if (gradient) {
							graphics._context2D.fillStyle = gradient;
							graphics.fill();
						}
						break;
					default:
						break;
					}
				} else {
					this.fill(graphics, useFormat.transparency);
				}
			}

			if (useFormat.line && isLineType) {
				graphics.stroke();
			}

			// JSGPRO
			if (serie.type === 'heatmap') {
				const colored = heatInfo.context.getImageData(0, 0, heatInfo.canvasPlot.width,
					heatInfo.canvasPlot.height);
				const pixels = colored.data;
				for (let i = 0, len = pixels.length, j; i < len; i += 4) {
					j = pixels[i + 3] * 4; // get gradient color from opacity value

					if (j) {
						pixels[i] = heatInfo.gradient[j];
						pixels[i + 1] = heatInfo.gradient[j + 1];
						pixels[i + 2] = heatInfo.gradient[j + 2];
					}
				}
				heatInfo.context.globalAlpha = 1;
				heatInfo.context.putImageData(colored, 0, 0);
				graphics.drawImage(heatInfo.canvasPlot, plotRect.left, plotRect.top);
			}
			// JSGENDPRO

			graphics.setLineWidth(-1);
			graphics.restore();

			if (serie.marker.style !== 'none') {
				const lineColor = serie.marker.lineColor || item.getTemplate().series.getLineForIndex(seriesIndex);
				const fillColor = serie.marker.fillColor || item.getTemplate().series.getFillForIndex(seriesIndex);
				index = 0;
				graphics.setLineColor(lineColor);
				graphics.setFillColor(fillColor);
				graphics.beginPath();

				while (item.getValue(ref, index, value)) {
					info.index = index;
					if (serie.marker.style === 'vertical') {
						value.y = axes.y.scale.min;
					}
					if (item.chart.dataMode === 'datainterrupt' || (value.x !== undefined && value.y !== undefined)) {
						pt.x = item.scaleToAxis(axes.x, value.x, undefined, false);
						pt.y = item.scaleToAxis(axes.y, value.y, info, false);
						switch (serie.type) {
						case 'profile':
						case 'line':
						case 'scatter':
							item.toPlot(serie, plotRect, pt);
							if (plotRect.containsPoint(pt)) {
								this.drawMarker(graphics, item, plotRect, serie, seriesIndex, index, {
									x: pt.x, y: pt.y
								}, serie.marker.size, horzChart, lineColor, fillColor);
							}
							break;
						default:
							break;
						}
					}
					index += 1;
				}
				graphics.fill();
				graphics.stroke();

			}

			return points;
		}

		drawLabel(graphics, item, labelRect, labelAngle, text, serie, lineHeight) {
			const center = labelRect.center;
			if (labelAngle !== 0) {
				graphics.translate(center.x, center.y);
				labelRect.translate(-center.x, -center.y);
				graphics.rotate(-labelAngle);
			}

			if (this.drawRect(graphics, labelRect, item, serie.dataLabel.format, 'serieslabel')) {
				item.setFont(graphics, serie.dataLabel.format, 'serieslabel', 'top',
					TextFormatAttributes.TextAlignment.CENTER);
			}
			if (labelAngle !== 0) {
				graphics.rotate(labelAngle);
				labelRect.translate(center.x, center.y);
				graphics.translate(-center.x, -center.y);
			}

			if (text instanceof Array) {
				let yLabel = labelRect.top + 75 + lineHeight / 2;
				text.forEach((part, pi) => {
					yLabel = center.y - ((text.length - 1) * lineHeight) / 2 + pi * (lineHeight + 50);
					const p = MathUtils.getRotatedPoint({
						x: center.x, y: yLabel
					}, center, -labelAngle);
					this.drawRotatedText(graphics, part, p.x, p.y, labelAngle, lineHeight);
				});
			} else {
				this.drawRotatedText(graphics, `${text}`, center.x, center.y, labelAngle, lineHeight);
			}
		}

		isRectUsed(usedRects, labelRect) {
			return usedRects.some(rect => {
				return rect.left <= labelRect.right &&
					labelRect.left <= rect.right &&
					rect.top <= labelRect.bottom &&
					labelRect.top <= rect.bottom
			});
		}

		drawLabels(graphics, item, plotRect, serie, seriesIndex, lastPoints, legendData) {
			let index = 0;
			const value = {
				formatX: {}, formatY: {}, formatC: {}
			};
			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);

			if (!ref || !axes) {
				return undefined;
			}

			const barWidth = item.getBarWidth(axes, serie, plotRect);
			const points = [];
			const pt = { x: 0, y: 0 };
			const info = {
				serie, seriesIndex, categories: axes.y.categories
			};
			const pieInfo = item.isCircular() ? item.getPieInfo(ref, serie, plotRect, seriesIndex) : undefined;
			const gaugeInfo = item.isGauge() ? item.getGaugeInfo(plotRect) : undefined;
			const labelAngle = serie.dataLabel.format.fontRotation === undefined ? 0 :
				JSG.MathUtils.toRadians(-serie.dataLabel.format.fontRotation);

			item.setFont(graphics, serie.dataLabel.format, 'serieslabel', 'top',
				TextFormatAttributes.TextAlignment.CENTER);
			const params = {
				graphics,
				serie,
				info,
				ref,
				axes,
				plotRect,
				barWidth,
				seriesIndex,
				points,
				lastPoints,
				pieInfo,
				gaugeInfo,
				currentAngle: pieInfo ? pieInfo.startAngle : 0,
				valueSum: 0
			};
			const textSize = item.measureText(graphics, graphics.getCoordinateSystem(), serie.dataLabel.format,
				'serieslabel', 'X');
			const lineHeight = textSize.height;
			const horizontalChart = item.isHorizontalChart();
			// eslint-disable-next-line no-unused-vars
			let valueSum = 0;
			const usedRects = [];

			if (serie.type === 'boxplot') {
				// JSGPRO
				if (item.hasDataPointLabel(serie, index)) {
					// collect values for each category
					const values = item.getBoxPlotValues(ref, axes);
					const drawLabelAtValue = (val, x, lbarWidth) => {
						const ptl = { x, y: item.scaleToAxis(axes.y, val.y, info, false) };
						item.toPlot(serie, plotRect, ptl);
						if (ptl.x >= plotRect.left && ptl.x <= plotRect.right) {
							const text = item.getDataLabel(val, -1, axes.x, ref, serie, legendData);
							params.boxBarWidth = lbarWidth;
							const labelRect = item.getLabelRect(ptl, val, text, index, params);
							if (labelRect) {
								this.drawLabel(graphics, item, labelRect, labelAngle, text, serie, lineHeight);
							}
						}
					};

					Object.entries(values).forEach(([, valueSet]) => {
						const { median, q1, q3, minIndex, maxIndex, average } = item.getBoxPlotFigures(valueSet);
						pt.x = item.scaleToAxis(axes.x, valueSet[0].x, undefined, false);
						const tmpVal = {
							x: valueSet[0].x, xLabel: valueSet[0].xLabel, y: q1, formatY: valueSet[0].formatY
						};

						drawLabelAtValue(tmpVal, pt.x, barWidth);
						tmpVal.y = median;
						drawLabelAtValue(tmpVal, pt.x, barWidth);
						tmpVal.y = q3;
						drawLabelAtValue(tmpVal, pt.x, barWidth);
						if (valueSet.length > 3) {
							// draw at whiskers
							drawLabelAtValue(valueSet[minIndex], pt.x, barWidth / 3);
							drawLabelAtValue(valueSet[maxIndex], pt.x, barWidth / 3);
						}
						if (serie.average) {
							tmpVal.y = average;
							drawLabelAtValue(tmpVal, pt.x, barWidth);
						}
						if (valueSet.length > 3) {
							valueSet.forEach((val, valIndex) => {
								if (valIndex !== maxIndex && valIndex !== minIndex) {
									if ((valIndex > minIndex && valIndex < maxIndex && serie.innerPoints) || valIndex < minIndex || (valIndex > maxIndex && serie.outerPoints)) {
										drawLabelAtValue(val, pt.x, 250);
									}
								}
							});
						}
					});
				}
			} else if (serie.type === 'map') {
				if (serie.map && serie.map.mapData) {
					const features = serie.map.mapData.features;
					const mapInfo = item.getMapInfo(plotRect, serie, ref);
					if (!mapInfo) {
						return undefined;
					}
					features.forEach((feature, mapIndex) => {
						index = item.findMapIndex(feature.properties, serie, mapInfo.labels);
						let text = feature.properties[serie.map.label] || '';
						if (index !== -1 && item.getValue(ref, index, value)) {
							value.x = text;
							text = item.getDataLabel(value, -1, axes.x, ref, serie, legendData);
						}
						const pm = item.getFeatureCenter(feature, mapInfo, serie, ref, index);
						const labelRect = item.getLabelRect(pm, value, text, index, params);
						if (item.hasDataPointLabel(serie, mapIndex)) {
							if (labelRect) {
								this.drawLabel(graphics, item, labelRect, labelAngle, text, serie, lineHeight);
							}
						}
					});
				}
				// JSGENDPRO
			} else {
				while (item.getValue(ref, index, value)) {
					info.index = index;
					if (value.x !== undefined && value.y !== undefined) {
						pt.x = item.scaleToAxis(axes.x, value.x, undefined, false);
						pt.y = item.scaleToAxis(axes.y, value.y, info, false);
						item.toPlot(serie, plotRect, pt);

						if (horizontalChart || (pt.x >= plotRect.left && pt.x <= plotRect.right)) {
							const y = value.y;
							if (serie.type === 'waterfall') {
								if (serie.autoSum && index) {
									const lastVal = { x: 0, y: 0 };
									item.getValue(ref, index - 1, lastVal);
									value.y -= lastVal.y === undefined ? 0 : lastVal.y;
								}
								if (serie.points[index] && serie.points[index].pointSum) {
									valueSum = value.y;
								} else {
									valueSum += value.y;
								}
							}
							const text = item.getDataLabel(value, index, axes.x, ref, serie, legendData);
							value.y = y;
							const labelRect = item.getLabelRect(pt, value, text, index, params);
							if (item.hasDataPointLabel(serie, index)) {
								if (labelRect) {
									if (!this.isRectUsed(usedRects, labelRect)) {
										this.drawLabel(graphics, item, labelRect, labelAngle, text, serie, lineHeight);
										usedRects.push(labelRect.expand(-100, -100))
									}
								}
							}
						}
					}
					index += 1;
				}
			}

			return params.points;
		}

		drawHiLoLines(graphics, item, plotRect) {
			let index = 0;
			const value = {};

			const serie = item.getFirstSerieOfType('line');
			if (!serie) {
				return;
			}

			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);

			if (!ref || !axes) {
				return;
			}

			graphics.beginPath();

			const fi = this.setFormat(graphics, item, item.chart.hiLoLines.format, 'hilolines');
			if (fi.line === false) {
				return;
			}

			const ptLow = { x: 0, y: 0 };
			const ptHigh = { x: 0, y: 0 };
			const info = {
				serie, seriesIndex: 0, categories: axes.y.categories
			};
			let tmp;

			while (item.getValue(ref, index, value)) {
				info.index = index;
				if (value.x !== undefined && value.y !== undefined) {
					ptLow.x = item.scaleToAxis(axes.x, value.x, undefined, false);
					ptHigh.x = ptLow.x;
					ptLow.y = undefined;
					ptHigh.y = undefined;
					for (let i = 0; i < item.series.length; i += 1) {
						if (item.series[i].type === 'line' && axes.y.categories[index].values[i] !== undefined) {
							tmp = axes.y.categories[info.index].values[i].y;
							if (Numbers.isNumber(tmp)) {
								ptLow.y = ptLow.y === undefined ? tmp : Math.min(ptLow.y, tmp);
								ptHigh.y = ptHigh.y === undefined ? tmp : Math.max(ptHigh.y, tmp);
							}
						}
					}
					if (ptLow.y !== undefined && ptHigh.y !== undefined) {
						ptLow.y = item.scaleToAxis(axes.y, ptLow.y, info, false);
						ptHigh.y = item.scaleToAxis(axes.y, ptHigh.y, info, false);
						item.toPlot(serie, plotRect, ptLow);
						item.toPlot(serie, plotRect, ptHigh);
						if (plotRect.containsPoint(ptLow)) {
							graphics.moveTo(ptLow.x, ptLow.y);
							graphics.lineTo(ptHigh.x, ptHigh.y);
						}
					}
				}
				index += 1;
			}
			graphics.stroke();
		}

		drawUpDownBars(graphics, item, plotRect) {
			let index = 0;
			const value = {};
			const indices = item.getFirstLastSerieIndicesOfType('line');
			if (indices.first === undefined || indices.last === undefined) {
				return;
			}
			const serie = item.series[indices.first];

			const ref = item.getDataSourceInfo(serie);
			const axes = item.getAxes(serie);
			if (!ref || !axes) {
				return;
			}

			const ptLow = { x: 0, y: 0 };
			const ptHigh = { x: 0, y: 0 };
			const rect = new ChartRect();
			const info = {
				serie, seriesIndex: 0, categories: axes.y.categories
			};
			let tmp;
			const barWidth = item.getUpDownBarWidth(axes, plotRect);

			while (item.getValue(ref, index, value)) {
				info.index = index;
				if (value.x !== undefined && value.y !== undefined) {
					ptLow.x = item.scaleToAxis(axes.x, value.x, undefined, false);
					ptHigh.x = ptLow.x;
					ptLow.y = undefined;
					ptHigh.y = undefined;
					if (axes.y.categories[index].values[indices.first] !== undefined) {
						tmp = axes.y.categories[info.index].values[indices.first].y;
						if (Numbers.isNumber(tmp)) {
							ptLow.y = tmp;
						}
					}
					if (axes.y.categories[index].values[indices.last] !== undefined) {
						tmp = axes.y.categories[info.index].values[indices.last].y;
						if (Numbers.isNumber(tmp)) {
							ptHigh.y = tmp;
						}
					}
					if (ptLow.y !== undefined && ptHigh.y !== undefined) {
						ptLow.y = item.scaleToAxis(axes.y, ptLow.y, info, false);
						ptHigh.y = item.scaleToAxis(axes.y, ptHigh.y, info, false);
						item.toPlot(serie, plotRect, ptLow);
						item.toPlot(serie, plotRect, ptHigh);
						if (plotRect.containsPoint(ptLow)) {
							rect.set(ptHigh.x - barWidth / 2, ptHigh.y, ptHigh.x + barWidth / 2, ptLow.y);
							if (ptLow.y > ptHigh.y) {
								this.drawRect(graphics, rect, item, item.chart.upBars.format, 'upbars');
							} else {
								this.drawRect(graphics, rect, item, item.chart.downBars.format, 'downbars');
							}
						}
					}
				}
				index += 1;
			}
		}

		drawTitle(graphics, item, title, id, angle) {
			if (!title.visible) {
				return;
			}

			const text = String(item.getExpressionValue(title.formula));

			this.drawRect(graphics, title.position, item, title.format, id);
			item.setFont(graphics, title.format, id, 'middle', TextFormatAttributes.TextAlignment.CENTER);

			if (angle) {
				graphics.translate(title.position.left + title.position.width / 2,
					title.position.top + title.position.height / 2 + 50);
				graphics.rotate(-angle);

				graphics.fillText(text, 0, 0);

				graphics.rotate(angle);
				graphics.translate(-(title.position.left + title.position.width / 2),
					-(title.position.top + title.position.height / 2 + 50));
			} else {
				graphics.fillText(text, title.position.left + title.position.width / 2,
					title.position.top + title.position.height / 2 + 50);
			}
		}

		drawMarker(graphics, item, plotRect, serie, seriesIndex, pointIndex, pos, defaultSize, horzChart, lineColor,
				   fillColor) {
			let size = defaultSize;
			let style = serie.marker.style;
			let pLineColor;
			let pFillColor;

			if (pointIndex !== undefined && serie.points.length) {
				if (serie.points[pointIndex] && serie.points[pointIndex].marker && serie.points[pointIndex].marker.lineColor !== undefined) {
					pLineColor = serie.points[pointIndex].marker.lineColor;
				}
				if (serie.points[pointIndex] && serie.points[pointIndex].marker && serie.points[pointIndex].marker.fillColor !== undefined) {
					pFillColor = serie.points[pointIndex].marker.fillColor;
				}
				if (serie.points[pointIndex] && serie.points[pointIndex].marker && serie.points[pointIndex].marker._size !== undefined) {
					size = serie.points[pointIndex].marker.size;
				}
				if (serie.points[pointIndex] && serie.points[pointIndex].marker && serie.points[pointIndex].marker._style !== undefined) {
					style = serie.points[pointIndex].marker.style;
				}
			}

			if (pLineColor || pFillColor) {
				graphics.fill();
				graphics.stroke();
				graphics.beginPath();
			}

			this.drawMarkerPath(graphics, plotRect, pos, style, size * 60, horzChart);

			if (pLineColor || pFillColor) {
				graphics.setLineColor(pLineColor || lineColor);
				graphics.setFillColor(pFillColor || fillColor);
				graphics.fill();
				graphics.stroke();
				graphics.beginPath();
			}

			if (pLineColor) {
				graphics.setLineColor(lineColor);
			}
			if (pFillColor) {
				graphics.setFillColor(fillColor);
			}
		}

		drawMarkerPath(graphics, plotRect, pos, style, size, horzChart) {
			// graphics.beginPath();

			switch (style) {
			case 'vertical':
				if (horzChart) {
					graphics.moveTo(plotRect.left, pos.y);
					graphics.lineTo(plotRect.right, pos.y);
				} else {
					graphics.moveTo(pos.x, plotRect.top);
					graphics.lineTo(pos.x, plotRect.bottom);
				}
				break;
			case 'circle':
				graphics.moveTo(pos.x + size / 2, pos.y);
				graphics.circle(pos.x, pos.y, size / 2);
				break;
			case 'cross':
				graphics.moveTo(pos.x - size / 2, pos.y);
				graphics.lineTo(pos.x + size / 2, pos.y);
				graphics.moveTo(pos.x, pos.y - size / 2);
				graphics.lineTo(pos.x, pos.y + size / 2);
				break;
			case 'crossRot':
				graphics.moveTo(pos.x - size / 2, pos.y - size / 2);
				graphics.lineTo(pos.x + size / 2, pos.y + size / 2);
				graphics.moveTo(pos.x + size / 2, pos.y - size / 2);
				graphics.lineTo(pos.x - size / 2, pos.y + size / 2);
				break;
			case 'dash':
				graphics.rect(pos.x - size / 2, pos.y - size / 6, size, size / 3);
				break;
			case 'dashright':
				graphics.rect(pos.x, pos.y - size / 6, size, size / 3);
				break;
			case 'line':
				graphics.moveTo(pos.x - size / 2, pos.y);
				graphics.lineTo(pos.x + size / 2, pos.y);
				break;
			case 'rect':
				graphics.rect(pos.x - size / 2, pos.y - size / 2, size, size);
				break;
			case 'rectRot':
				graphics.moveTo(pos.x, pos.y - size / 2);
				graphics.lineTo(pos.x + size / 2, pos.y);
				graphics.lineTo(pos.x, pos.y + size / 2);
				graphics.lineTo(pos.x - size / 2, pos.y);
				graphics.closePath();
				break;
			case 'star':
				graphics.moveTo(pos.x - size / 2, pos.y);
				graphics.lineTo(pos.x + size / 2, pos.y);
				graphics.moveTo(pos.x, pos.y - size / 2);
				graphics.lineTo(pos.x, pos.y + size / 2);
				graphics.moveTo(pos.x - size / 2, pos.y - size / 2);
				graphics.lineTo(pos.x + size / 2, pos.y + size / 2);
				graphics.moveTo(pos.x + size / 2, pos.y - size / 2);
				graphics.lineTo(pos.x - size / 2, pos.y + size / 2);
				break;
			case 'triangle':
				graphics.moveTo(pos.x, pos.y - size / 2);
				graphics.lineTo(pos.x + size / 2, pos.y + size / 2);
				graphics.lineTo(pos.x - size / 2, pos.y + size / 2);
				graphics.closePath();
				break;
			default:
				break;
			}
		}

		prepareHeatmap(graphics, chartValueRanges, plotRect) {
			const blur = 15;
			const r = 25;

			const circle = document.createElement('canvas');
			let ctx = circle.getContext('2d');
			const r2 = r + blur;

			circle.width = r2 * 2;
			circle.height = r2 * 2;

			ctx.shadowOffsetX = r2 * 2;
			ctx.shadowOffsetY = r2 * 2;
			ctx.shadowBlur = blur;
			ctx.shadowColor = 'black';

			ctx.beginPath();
			ctx.arc(-r2, -r2, r, 0, Math.PI * 2, true);
			ctx.closePath();
			ctx.fill();

			// create a 256x1 gradient that we'll use to turn a grayscale heatmap into a colored one
			const canvas = document.createElement('canvas');
			ctx = canvas.getContext('2d');
			const gradient = ctx.createLinearGradient(0, 0, 0, 256);

			canvas.width = 1;
			canvas.height = 256;

			if (chartValueRanges && chartValueRanges.length) {
				chartValueRanges.forEach((range) => {
					if (String(range.color).length) {
						try {
							gradient.addColorStop(range.from, String(range.color));
						} catch (e) {
							gradient.addColorStop(0, '#CCCCCC');
						}
					}
				});
			} else {
				gradient.addColorStop(0.4, 'blue');
				gradient.addColorStop(0.6, 'cyan');
				gradient.addColorStop(0.7, 'lime');
				gradient.addColorStop(0.8, 'yellow');
				gradient.addColorStop(1, 'red');
			}

			ctx.fillStyle = gradient;
			ctx.fillRect(0, 0, 1, 256);

			const image = ctx.getImageData(0, 0, 1, 256).data;

			const canvasPlot = document.createElement('canvas');
			const context = canvasPlot.getContext('2d');
			const cs = graphics.getCoordinateSystem();
			canvasPlot.width = cs.logToDeviceX(plotRect.width);
			canvasPlot.height = cs.logToDeviceX(plotRect.height);

			context.clearRect(0, 0, this._width, this._height);
			return {
				circle, canvasPlot, context, cs: graphics.getCoordinateSystem(), gradient: image, r: r2
			};
		}

		getSelectedFormat() {
			let f;

			if (this.chartSelection) {
				f = new FormatAttributes();
				const data = this.getItem().getDataFromSelection(this.chartSelection);
				const template = this.getItem().getTemplate();
				if (data) {
					switch (this.chartSelection.element) {
					case 'serieslabel':
						f.fillcolor =
							data.dataLabel.format.fillColor || template[this.chartSelection.element].format.fillColor;
						f.fillstyle = data.dataLabel.format.fillStyle === undefined ?
							template[this.chartSelection.element].format.fillStyle : data.dataLabel.format.fillStyle;
						f.transparency = data.dataLabel.format.transparency === undefined ? 100 :
							data.dataLabel.format.transparency;
						f.linecolor =
							data.dataLabel.format.lineColor || template[this.chartSelection.element].format.lineColor;
						f.linestyle =data.dataLabel.format.lineStyle === undefined ?
							template[this.chartSelection.element].format.lineStyle : data.dataLabel.format.lineStyle;
						f.linewidth =data.dataLabel.format.lineWidth === undefined ?
							template[this.chartSelection.element].format.lineWidth : data.dataLabel.format.lineWidth;
						break;
					case 'hilolines':
					case 'upbars':
					case 'downbars':
					case 'plot':
					case 'title':
					case 'legend':
						f.fillcolor = data.format.fillColor || template[this.chartSelection.element].format.fillColor;
						f.fillstyle = data.format.fillStyle === undefined ?
							template[this.chartSelection.element].format.fillStyle : data.format.fillStyle;
						f.transparency = data.format.transparency === undefined ? 100 : data.format.transparency;
						f.linecolor = data.format.lineColor || template[this.chartSelection.element].format.lineColor;
						f.linestyle =data.format.lineStyle === undefined ?
							template[this.chartSelection.element].format.lineStyle : data.format.lineStyle;
						f.linewidth =data.format.lineWidth === undefined ?
							template[this.chartSelection.element].format.lineWidth : data.format.lineWidth;
						break;
					case 'point': {
						let pointFormat;
						let lineColor;
						let lineStyle;
						let lineWidth;
						let fillColor;
						let fillStyle;
						let transparency;

						if (data.points[this.chartSelection.pointIndex] && data.points[this.chartSelection.pointIndex].format) {
							pointFormat = data.points[this.chartSelection.pointIndex].format;
							lineColor = pointFormat.lineColor || data.format.lineColor;
							lineWidth = pointFormat.lineWidth || data.format.lineWidth;
							lineStyle = pointFormat.lineStyle || data.format.lineStyle;
							fillColor = pointFormat.fillColor || data.format.fillColor;
							fillStyle = pointFormat.fillStyle || data.format.fillStyle;
							transparency = pointFormat.transparency || data.format.transparency;
						} else {
							lineColor = data.format.lineColor;
							lineWidth = data.format.lineWidth;
							lineStyle = data.format.lineStyle;
							fillColor = data.format.fillColor;
							fillStyle = data.format.fillStyle;
							transparency = data.format.transparency;
						}

						f.fillcolor = fillColor || template.series.getFillForIndex(this.chartSelection.index);
						f.fillstyle = fillStyle === undefined ? template.series.fillstyle : fillStyle;
						f.transparency = transparency === undefined ? 100 : transparency;
						f.linecolor = lineColor || template.series.getLineForIndex(this.chartSelection.index);
						f.linestyle =lineStyle === undefined ? template.series.linestyle : lineStyle;
						f.linewidth =lineWidth === undefined ? template.series.linewidth : lineWidth;
					}
						break;
					case 'series':
						f.fillcolor =
							data.format.fillColor || template.series.getFillForIndex(this.chartSelection.index);
						f.fillstyle =
							data.format.fillStyle === undefined ? template.series.fillstyle : data.format.fillStyle;
						f.transparency = data.format.transparency === undefined ? 100 : data.format.transparency;
						f.linecolor =
							data.format.lineColor || template.series.getLineForIndex(this.chartSelection.index);
						f.linestyle =
							data.format.lineStyle === undefined ? template.series.linestyle : data.format.lineStyle;
						f.linewidth =
							data.format.lineWidth === undefined ? template.series.linewidth : data.format.lineWidth;
						break;
					case 'xAxis':
					case 'yAxis':
						f.fillcolor = data.format.fillColor || template.axis.format.fillColor;
						f.fillstyle = data.format.fillStyle === undefined ? template.axis.format.fillStyle :
							data.format.fillStyle;
						f.transparency = data.format.transparency === undefined ? 100 : data.format.transparency;
						f.linecolor = data.format.lineColor || template.axis.format.lineColor;
						f.linestyle =data.format.lineStyle === undefined ? template.axis.format.lineStyle :
							data.format.lineStyle;
						f.linewidth =data.format.lineWidth === undefined ? template.axis.format.lineWidth :
							data.format.lineWidth;
						break;
					case 'xAxisGrid':
					case 'yAxisGrid':
						f.fillcolor = data.formatGrid.fillColor || template.axisgrid.format.fillColor;
						f.fillstyle = data.formatGrid.fillStyle === undefined ? template.axisgrid.format.fillStyle :
							data.formatGrid.fillStyle;
						f.transparency =
							data.formatGrid.transparency === undefined ? 100 : data.formatGrid.transparency;
						f.linecolor = data.formatGrid.lineColor || template.axisgrid.format.lineColor;
						f.linestyle =data.formatGrid.lineStyle === undefined ? template.axisgrid.format.lineStyle :
							data.formatGrid.lineStyle;
						f.linewidth =data.formatGrid.lineWidth === undefined ? template.axisgrid.format.lineWidth :
							data.formatGrid.lineWidth;
						break;
					case 'xAxisTitle':
					case 'yAxisTitle':
						f.fillcolor = data.format.fillColor || template.axisTitle.format.fillColor;
						f.fillstyle =
							data.format.fillStyle === undefined ? template.axisTitle.fillStyle : data.format.fillStyle;
						f.transparency = data.format.transparency === undefined ? 100 : data.format.transparency;
						f.linecolor = data.format.lineColor || template.axisTitle.format.lineColor;
						f.linestyle =
							data.format.lineStyle === undefined ? template.axisTitle.lineStyle : data.format.lineStyle;
						f.linewidth =
							data.format.lineWidth === undefined ? template.axisTitle.lineWidth : data.format.lineWidth;
						break;
					default:
						break;
					}
				}
			}

			return f;
		}

		getSelectedTextFormat() {
			let tf;

			if (this.chartSelection) {
				tf = {};
				const data = this.getItem().getDataFromSelection(this.chartSelection);
				const template = this.getItem().getTemplate();
				if (data) {
					switch (this.chartSelection.element) {
					case 'serieslabel':
						tf.fontname =
							data.dataLabel.format.fontName || template[this.chartSelection.element].format.fontName || template.font.name;
						tf.fontsize =
							data.dataLabel.format.fontSize || template[this.chartSelection.element].format.fontSize || template.font.size;
						if (data.dataLabel.format.fontStyle !== undefined) {
							tf.fontstyle = data.dataLabel.format.fontStyle;
						} else if (template[this.chartSelection.element].format.fontStyle !== undefined) {
							tf.fontstyle = template[this.chartSelection.element].format.fontStyle;
						} else {
							tf.fontstyle = template.font.style;
						}
						tf.numberformat =
							data.dataLabel.format.numberFormat || template[this.chartSelection.element].format.numberFormat || 'General';
						tf.localculture =
							data.dataLabel.format.localCulture || template[this.chartSelection.element].format.localCulture || 'general';
						break;
					case 'series':
					case 'title':
					case 'legend':
						tf.fontname =
							data.format.fontName || template[this.chartSelection.element].format.fontName || template.font.name;
						tf.fontsize =
							data.format.fontSize || template[this.chartSelection.element].format.fontSize || template.font.size;
						if (data.format.fontStyle !== undefined) {
							tf.fontstyle = data.format.fontStyle;
						} else if (template[this.chartSelection.element].format.fontStyle !== undefined) {
							tf.fontstyle = template[this.chartSelection.element].format.fontStyle;
						} else {
							tf.fontstyle = template.font.style;
						}
						tf.numberformat =
							data.format.numberFormat || template[this.chartSelection.element].format.numberFormat || 'General';
						tf.localculture =
							data.format.localCulture || template[this.chartSelection.element].format.localCulture || 'general';
						break;
					case 'xAxis':
					case 'yAxis':
						tf.fontname = data.format.fontName || template.axis.format.fontName || template.font.name;
						tf.fontsize = data.format.fontSize || template.axis.format.fontSize || template.font.size;
						if (data.format.fontStyle !== undefined) {
							tf.fontstyle = data.format.fontStyle;
						} else if (template.axis.format.fontStyle !== undefined) {
							tf.fontstyle = template.axis.format.fontStyle;
						} else {
							tf.fontstyle = template.font.style;
						}
						tf.numberformat = data.format.numberFormat || template.axis.format.numberFormat || 'General';
						tf.localculture = data.format.localCulture || template.axis.format.localCulture || 'general';
						break;
					case 'xAxisTitle':
					case 'yAxisTitle':
						tf.fontname =
							data.format.fontName || template.axisTitle.format.fontName || template.font.name;
						tf.fontsize =
							data.format.fontSize || template.axisTitle.format.fontSize || template.font.size;
						if (data.format.fontStyle !== undefined) {
							tf.fontstyle = data.format.fontStyle;
						} else if (template.axisTitle.format.fontStyle !== undefined) {
							tf.fontstyle = template.axisTitle.format.fontStyle;
						} else {
							tf.fontstyle = template.font.style;
						}
						break;
					default:
						break;
					}
				}
			}

			return tf;
		}

		hasSelectedFormula() {
			if (this.chartSelection) {
				switch (this.chartSelection.element) {
				case 'series':
				case 'title':
				case 'plot':
				case 'legend':
				case 'xAxis':
				case 'xAxisTitle':
				case 'yAxis':
				case 'yAxisTitle':
					return true;
				default:
					return false;
				}
			}

			return false;
		}

		getSelectedFormula(sheet) {
			let expr;

			if (this.chartSelection) {
				switch (this.chartSelection.element) {
				case 'series':
				case 'xAxis':
				case 'yAxis':
				case 'title':
				case 'xAxisTitle':
				case 'yAxisTitle': {
					const data = this.getItem().getDataFromSelection(this.chartSelection);
					if (!data) {
						return super.getSelectedFormula(sheet);
					}
					expr = data.formula;
					break;
				}
				case 'legend':
					expr = this.getItem().legend.formulaTitle;
					break;
				case 'plot':
					expr = this.getItem().chart.formula;
					break;
				default:
					break;
				}
			}

			if (expr) {
				if (expr.getTerm()) {
					return `=${expr.getTerm().toLocaleString(JSG.getParserLocaleSettings(), {
						sheet: sheet.model, useName: true
					})}`;
				}
				return String(expr.getValue());
			}

			return super.getSelectedFormula(sheet);
		}

		applyAttributes(map, viewer) {
			const update = (key) => {
				const cmd = this.getItem().prepareCommand(key);
				const data = this.getItem().getDataFromSelection(this.chartSelection);
				if (!data) {
					return;
				}
				let format;
				switch (this.chartSelection.element) {
				case 'xAxisGrid':
				case 'yAxisGrid':
					format = data.formatGrid;
					break;
				case 'point': {
					const point = this.getItem().getDataPoint(data, this.chartSelection.pointIndex);
					if (!point.format) {
						point.format = new ChartFormat();
					}
					format = point.format;
					break;
				}
				case 'serieslabel':
					format = data.dataLabel.format;
					break;
				default:
					format = data.format;
					break;
				}
				let value = map.get('linecolor');
				if (value) {
					if (value === 'auto' && format.line) {
						format.line.color = undefined;
					} else if (value !== 'auto') {
						format.lineColor = value;
					}
				}
				value = map.get('linestyle');
				if (value !== undefined) {
					format.lineStyle = value;
				}
				value = map.get('linewidth');
				if (value) {
					format.lineWidth = value;
				}
				value = map.get('fillcolor');
				if (value) {
					if (value === 'auto' && format.fill) {
						format.fill.color = undefined;
					} else if (value !== 'auto') {
						format.fillColor = value;
					}
				}
				value = map.get('fillstyle');
				if (value !== undefined) {
					format.fillStyle = value;
				}
				value = map.get('transparency');
				if (value !== undefined) {
					format.transparency = value;
				}
				value = map.get('fontcolor');
				if (value) {
					format.fontColor = value;
				}
				value = map.get('fontname');
				if (value) {
					format.fontName = value;
				}
				value = map.get('fontsize');
				if (value) {
					format.fontSize = Number(value);
				}
				value = map.get('fontstyle');
				if (value !== undefined) {
					format.fontStyle = Number(value);
				}
				value = map.get('numberformat');
				if (value === 'General') {
					format.numberFormat = undefined;
					format.localCulture = undefined;
				} else {
					if (value !== undefined) {
						format.linkNumberFormat = false;
						format.numberFormat = map.get('numberformat');
					}
					value = map.get('localculture');
					if (value !== undefined) {
						format.localCulture = map.get('localculture');
					}
				}
				if (this.chartSelection.element === 'series') {
					data.points.forEach((point) => {
						if (point.format) {
							if (point.format.fill) {
								value = map.get('fillcolor');
								if (value) {
									point.format.fill.color = undefined;
								}
								value = map.get('fillstyle');
								if (value) {
									point.format.fill.style = undefined;
								}
							}
							if (point.format.line) {
								value = map.get('linecolor');
								if (value) {
									point.format.line.color = undefined;
								}
								value = map.get('linestyle');
								if (value) {
									point.format.line.style = undefined;
								}
								value = map.get('linewidth');
								if (value) {
									point.format.line.width = undefined;
								}
							}
						}
					});
				}

				this.getItem().finishCommand(cmd, key);
				viewer.getInteractionHandler().execute(cmd);
			};

			if (this.chartSelection) {
				switch (this.chartSelection.element) {
				case 'series':
				case 'point':
				case 'serieslabel':
					update('series');
					return true;
				case 'xAxis':
				case 'yAxis':
				case 'xAxisTitle':
				case 'yAxisTitle':
				case 'xAxisGrid':
				case 'yAxisGrid':
					update('axes');
					return true;
				case 'title':
					update('title');
					return true;
				case 'legend':
					update('legend');
					return true;
				case 'plot':
					update('plot');
					return true;
				case 'hilolines':
				case 'upbars':
				case 'downbars':
					update('chart');
					return true;
				default:
					break;
				}
			}
			return false;
		}

		getSheetView() {
			let sheet = this;

			while (sheet && sheet.getItem && !sheet.getItem().isStreamSheet) {
				sheet = sheet.getParent();
			}

			return sheet;
		}

		lightenDarkenColor(color, amt) {
			const arr = [];
			const toHex = (int) => {
				const hex = int.toString(16);
				return hex.length === 1 ? `0${hex}` : hex;
			};
			color.replace(/[\d+.]+/g, (v) => {
				arr.push(parseFloat(v));
			});

			const col = arr
				.slice(0, 3)
				.map(toHex)
				.join('');

			const num = parseInt(col, 16);
			let r = (num >> 16) + amt;
			if (r > 255) {
				r = 255;
			} else if (r < 0) {
				r = 0;
			}
			let b = ((num >> 8) & 0x00ff) + amt;
			if (b > 255) {
				b = 255;
			} else if (b < 0) {
				b = 0;
			}
			let g = (num & 0x0000ff) + amt;
			if (g > 255) {
				g = 255;
			} else if (g < 0) {
				g = 0;
			}
			return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
		}

		getSelectedPropertyCategory() {
			const selection = this.chartSelection;
			if (selection) {
				switch (selection.element) {
				case 'xAxis':
				case 'yAxis':
					return 'axes';
				case 'xAxisGrid':
				case 'yAxisGrid':
					return 'axesgrid';
				case 'xAxisTitle':
				case 'yAxisTitle':
					return 'axestitle';
				default:
					return selection.element;
				}
			}

			return undefined;
		}

		setSelectedPropertyCategory(data, index) {
			const item = this.getItem();

			switch (data[0]) {
			case 'geometry':
			case 'chart':
				this.chartSelection = undefined;
				break;
			case 'plot':
				this.chartSelection = {
					element: 'plot', data: item.plot
				};
				break;
			case 'legend':
				this.chartSelection = {
					element: 'legend', data: item.legend
				};
				break;
			case 'title':
				this.chartSelection = {
					element: 'title', data: item.title
				};
				break;
			case 'series':
				this.chartSelection = {
					element: 'series', index, selectionIndex: 0, dataPoints: [], data: item.series[index]
				};
				break;
			case 'serieslabel':
				this.chartSelection = {
					element: 'serieslabel', index, selectionIndex: 0, dataPoints: [], data: item.series[index]
				};
				break;
			case 'axes':
				this.chartSelection = {
					element: 'xAxis', index, data: item.xAxes[index]
				};
				break;
			case 'axesgrid':
				this.chartSelection = {
					element: 'xAxisGrid', index, data: item.xAxes[index]
				};
				break;
			case 'axestitle':
				this.chartSelection = {
					element: 'xAxisTitle', index, data: item.xAxes[index].title
				};
				break;
			case 'point':
				this.chartSelection = {
					element: 'point',
					index: 0,
					selectionIndex: 0,
					pointIndex: 0,
					dataPoints: [],
					data: item.series[index]
				};
				break;
			default:
				this.chartSelection = undefined;
				break;
			}

			if (this.chartSelection) {
				const layer = this.getGraphView().getLayer('chartselection');
				layer.push(new JSG.ChartSelectionFeedbackView(this));
			} else {
				this.getGraphView().clearLayer('chartselection');
			}
		}

		getDragTarget() {
			return this;
		}

		getFeedback(location, startLocation, title, sourceView, key, event, viewer) {
			return sourceView ? new JSG.ChartDragFeedbackView(this, location, sourceView, viewer) : undefined;
		}

		onDrop(feedback, title, sourceView, event, viewer) {
			if (feedback.action === undefined || feedback.action === 'none') {
				return;
			}

			this.getItem().createSeriesFromInbox(feedback, sourceView.getItem(), viewer);
		}
	}

	return new SheetPlotView(...args);
}
